import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Wrench,
    Hash,
    Plus,
    Search,
    Edit,
    Trash2,
    Save,
    X,
    MessageSquare,
    HelpCircle,
    ChevronRight,
    Clock,
    User,
    Sparkles,
    LayoutGrid,
    Percent,
    ShieldAlert,
    Gavel,
    RefreshCw,
    History,
    SearchX,
    Megaphone,
    CheckCircle2,
    Scale,
    FileVideo,
    Globe,
    FileImage,
    Link,
    AlertTriangle
} from 'lucide-react';
import StaffDosimetry from './StaffDosimetry';
import StaffResponsePatterns from './StaffResponsePatterns';
import StaffTicketVerification from './StaffTicketVerification';
import R2Upload from './R2Upload';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTickets } from '../../contexts/TicketContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePunishment } from '../../contexts/PunishmentContext';
import { PUNISHMENT_TYPES, calculateExpiration } from '../../utils/punishmentUtils';
import { safeParseArray, safeParseObject } from '../../utils/storageUtils.js';

import PlayerProfileView from './PlayerProfileView';
import { getStaffDisplayName } from '../../utils/staffDisplay';


const StaffTools = () => {
    const { user, allUsers, ensurePreRegisteredUser } = useAuth();
    const staffName = getStaffDisplayName(user);
    const { faqs, addFAQ, updateFAQ, deleteFAQ, createTicket, claimTicket, addTicketResponse, getUserTickets, deleteTicket } = useTickets();
    const {
        addPunishment: executePunishment,
        getAllPunishments,
        revokePunishment,
        deletePunishment // Physical delete
    } = usePunishment(); // Hook for punishments
    const isLegalDept = user?.accessLevel === 'departamento_juridico';
    const [activeTab, setActiveTab] = useState(isLegalDept ? 'verification' : 'faq-inteligente');

    // Force tab if role is legal dept
    useEffect(() => {
        if (isLegalDept && activeTab !== 'verification') {
            setActiveTab('verification');
        }
    }, [isLegalDept, activeTab]);

    // ========== FAQ INTELIGENTE ==========
    const [isEditingFAQ, setIsEditingFAQ] = useState(false);
    const [selectedFAQ, setSelectedFAQ] = useState(null);
    const [faqForm, setFaqForm] = useState({ question: '', answer: '', category: 'suporte_duvidas' });
    const [faqSearch, setFaqSearch] = useState('');

    const handleSaveFAQ = () => {
        if (!faqForm.question || !faqForm.answer) {
            toast.error('Preencha a pergunta e a resposta.');
            return;
        }

        if (selectedFAQ) {
            updateFAQ(selectedFAQ.id, faqForm);
            toast.success('FAQ atualizado com sucesso!');
        } else {
            addFAQ(faqForm);
            toast.success('FAQ criado com sucesso!');
        }

        setIsEditingFAQ(false);
        setSelectedFAQ(null);
        setFaqForm({ question: '', answer: '', category: 'suporte_duvidas' });
    };

    const handleEditFAQ = (faq) => {
        setSelectedFAQ(faq);
        setFaqForm({ question: faq.question, answer: faq.answer, category: faq.category });
        setIsEditingFAQ(true);
    };

    const handleDeleteFAQ = (id) => {
        if (window.confirm('Tem certeza que deseja excluir este FAQ?')) {
            deleteFAQ(id);
        }
    };

    const filteredFaqs = faqs.filter(f =>
        f.question.toLowerCase().includes(faqSearch.toLowerCase()) ||
        f.answer.toLowerCase().includes(faqSearch.toLowerCase())
    );

    // Mapeamento de categorias para cores/icones
    const getCategoryStyle = (cat) => {
        const styles = {
            'suporte_duvidas': { color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20', icon: HelpCircle, label: 'Dúvidas Gerais' },
            'compras_doacoes': { color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20', icon: Sparkles, label: 'Problema com Compras' },
            'denuncia_jogador': { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20', icon: MessageSquare, label: 'Denunciar Jogador' },
            'contestar_punicao': { color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20', icon: Wrench, label: 'Revogar Punição' },
            'programacao': { color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20', icon: LayoutGrid, label: 'Problema Técnico / Bug' },
            'cupons_descontos': { color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20', icon: Percent, label: 'Cupom ou Desconto' },
            'denuncia_staff': { color: 'text-red-500', bg: 'bg-red-600/10', border: 'border-red-600/20', icon: ShieldAlert, label: 'Denúncia contra Staff' },
            'contestar_banimento': { color: 'text-orange-500', bg: 'bg-orange-600/10', border: 'border-orange-600/20', icon: Gavel, label: 'Revogar Banimento' },
        };
        return styles[cat] || styles['suporte_duvidas'];
    };

    // ========== CENTRAL DE IDS ==========
    const [activeIdTab, setActiveIdTab] = useState('CAPITAL');
    const [searchId, setSearchId] = useState('');
    const [isCreatingId, setIsCreatingId] = useState(false);
    const [newIdData, setNewIdData] = useState({ id: '', city: 'CAPITAL' });
    const [isRefreshingIds, setIsRefreshingIds] = useState(false);

    // ========== APLICAR PUNIÇÃO ==========
    const [punishmentSearch, setPunishmentSearch] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [showNoProofWarning, setShowNoProofWarning] = useState(false);
    const [pendingPunishment, setPendingPunishment] = useState(null);

    const [selectedCharacter, setSelectedCharacter] = useState(null);
    const [punishmentForm, setPunishmentForm] = useState({
        type: 'Advertência',
        reason: '',
        duration: '',
        durationType: 'days', // days, hours
        isIndefinite: false,
        notes: '',
        directDiscordId: '', // For direct Discord ID input when type is Blacklist de Tickets
        evidencePath: null, // Path to Supabase Storage evidence
        attachments: [], // Array of attachment paths
        // Manual mode fields
        isManualMode: false,
        manualCity: 'CAPITAL',
        manualCharacterId: '',
        manualDiscordId: '',
        externalProofUrl: ''
    });
    const [isApplyingPunishment, setIsApplyingPunishment] = useState(false);

    // Validation Check
    const isPunishmentFormValid = React.useMemo(() => {
        // 1. User/Character must be selected (or manual mode valid)
        const hasTarget = (selectedUser && (selectedCharacter || punishmentForm.type === 'Blacklist de Tickets')) ||
            (punishmentForm.isManualMode && punishmentForm.manualDiscordId && punishmentForm.manualCharacterId && punishmentForm.manualCity);

        // 2. Basic fields
        const hasType = !!punishmentForm.type;
        const hasReason = !!punishmentForm.reason;
        const hasNotes = !!punishmentForm.notes;

        // 3. Duration (if not indefinite/blacklist)
        const needsDuration = !punishmentForm.isIndefinite && punishmentForm.type !== 'Blacklist de Tickets' && punishmentForm.type !== 'Advertência Verbal';
        const hasDuration = !needsDuration || (punishmentForm.duration && punishmentForm.duration > 0);

        return hasTarget && hasType && hasReason && hasNotes && hasDuration;
    }, [selectedUser, selectedCharacter, punishmentForm]);




    // ========== GERENCIAR PUNIÇÕES ==========
    const [managePunishmentSearch, setManagePunishmentSearch] = useState('');
    const [punishmentProfiles, setPunishmentProfiles] = useState([]); // New: List of found profiles
    const [selectedHistoryProfile, setSelectedHistoryProfile] = useState(null); // New: Selected profile for history
    const [historyResults, setHistoryResults] = useState([]);
    const [isSearchingHistory, setIsSearchingHistory] = useState(false);

    // Evidence Modal State
    const [evidenceModalOpen, setEvidenceModalOpen] = useState(false);
    const [evidenceUrl, setEvidenceUrl] = useState('');
    const [evidenceType, setEvidenceType] = useState('image'); // 'image' or 'video'
    const [evidenceLoading, setEvidenceLoading] = useState(false);
    const [evidenceFileKey, setEvidenceFileKey] = useState(null);

    const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
    const [punishmentToRevoke, setPunishmentToRevoke] = useState(null);
    const [revocationReason, setRevocationReason] = useState('');
    const [searchedHistoryUser, setSearchedHistoryUser] = useState(null); // Deprecated but kept for compatibility if needed, though we should transition to selectedHistoryProfile
    const [activePunishmentsCount, setActivePunishmentsCount] = useState(0);
    const [summonsCount, setSummonsCount] = useState(0);

    // ========== INTIMAR USUÁRIO ==========
    const [summonSearch, setSummonSearch] = useState('');
    const [selectedSummonUser, setSelectedSummonUser] = useState(null);
    const [selectedSummonCharacter, setSelectedSummonCharacter] = useState(null);
    const [summonForm, setSummonForm] = useState({
        reason: '',
        publicText: '',
        internalText: ''
    });
    const [isSummoning, setIsSummoning] = useState(false);

    // ========== BUSCAR JOGADOR ==========
    const [playerSearchQuery, setPlayerSearchQuery] = useState('');
    const [playerSearchResults, setPlayerSearchResults] = useState([]);
    const [isSearchingPlayers, setIsSearchingPlayers] = useState(false);
    const [selectedPlayerProfile, setSelectedPlayerProfile] = useState(null);

    // Helper para normalizar strings (remover acentos e colocar em CAPS)
    const normalizeStr = (str) => {
        if (!str) return '';
        return String(str).trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
    };

    const handlePlayerSearch = (query) => {
        setPlayerSearchQuery(query);
        if (!query || query.length < 2) {
            setPlayerSearchResults([]);
            return;
        }

        setIsSearchingPlayers(true);
        // Simular delay para UX
        setTimeout(() => {
            const term = query.toLowerCase();
            const usersArray = Array.isArray(allUsers) ? allUsers : Object.values(allUsers || {});

            const results = usersArray.filter(u => {
                const nameMatch = u.name?.toLowerCase().includes(term);
                const discordIdMatch = u.discordId?.toString().includes(term);
                const charNameMatch = u.cities?.some(c => c.characterName?.toLowerCase().includes(term));
                const charIdMatch = u.cities?.some(c => c.playerId?.toString().includes(term));

                return nameMatch || discordIdMatch || charNameMatch || charIdMatch;
            });

            setPlayerSearchResults(results);
            setIsSearchingPlayers(false);
        }, 300);
    };


    // Filter users for punishment search
    const getFilteredUsers = () => {
        if (!punishmentSearch || punishmentSearch.length < 2) return [];

        const term = punishmentSearch.toLowerCase();
        // Convert allUsers object to array if it's an object, or use it directly if it's an array
        const usersArray = Array.isArray(allUsers) ? allUsers : Object.values(allUsers || {});

        return usersArray.filter(u => {
            const name = u.name?.toLowerCase() || '';
            const discordId = u.discordId?.toString() || '';
            const charName = u.cities?.some(c => c.characterName?.toLowerCase().includes(term));
            const charId = u.cities?.some(c => c.playerId?.toString().includes(term));

            return name.includes(term) || discordId.includes(term) || charName || charId;
        }).slice(0, 5); // Limit results
    };

    const handleSelectUser = (user) => {
        setSelectedUser(user);
        setSelectedCharacter(null); // Reset character selection when user changes
        setPunishmentSearch('');
    };

    const preparePunishmentPayload = async () => {
        const isTicketBlacklist = punishmentForm.type === 'Blacklist de Tickets';
        const isManualMode = punishmentForm.isManualMode;

        // Validation
        if (!selectedUser && !isTicketBlacklist && !isManualMode) {
            toast.error('Por favor, selecione um usuário.');
            return null;
        }
        if (isTicketBlacklist && !selectedUser && !punishmentForm.directDiscordId && !isManualMode) {
            toast.error('Por favor, selecione um usuário ou informe o Discord ID diretamente.');
            return null;
        }
        if (isManualMode) {
            if (!punishmentForm.manualDiscordId) {
                toast.error('Por favor, informe o Discord ID do usuário.');
                return null;
            }
            if (!punishmentForm.manualCharacterId) {
                toast.error('Por favor, informe o ID do personagem.');
                return null;
            }
        }
        if (!punishmentForm.reason) {
            toast.error('Por favor, informe o motivo da punição (Motivo Público).');
            return null;
        }
        if (!punishmentForm.notes) {
            toast.error('Por favor, adicione as notas internas (Obrigatório).');
            return null;
        }
        if (!isTicketBlacklist && !selectedCharacter && !isManualMode) {
            toast.error('Por favor, selecione em qual perfil a punição será aplicada.');
            return null;
        }

        let punishmentData = {};
        let targetUserData = null;

        try {
            const targetUserId = isManualMode ? punishmentForm.manualDiscordId : (selectedUser?.discordId || punishmentForm.directDiscordId);
            targetUserData = selectedUser;

            if (isManualMode) {
                targetUserData = {
                    discordId: punishmentForm.manualDiscordId,
                    name: 'Usuário Desconhecido (Pré-Registro)'
                };
                if (ensurePreRegisteredUser) {
                    await ensurePreRegisteredUser(punishmentForm.manualDiscordId, 'Usuário Desconhecido (Pré-Registro)');
                }
            } else if (!targetUserData && punishmentForm.directDiscordId) {
                const usersArray = Array.isArray(allUsers) ? allUsers : Object.values(allUsers || {});
                targetUserData = usersArray.find(u => u.discordId === punishmentForm.directDiscordId) || {
                    discordId: punishmentForm.directDiscordId,
                    name: 'Usuário não cadastrado'
                };
            }

            punishmentData.userId = targetUserId;

            if (isManualMode) {
                punishmentData.characterId = punishmentForm.manualCharacterId;
                punishmentData.characterName = 'Usuário Desconhecido';
                punishmentData.city = punishmentForm.manualCity;
            } else {
                punishmentData.characterId = selectedCharacter?.playerId || null;
                punishmentData.characterName = selectedCharacter?.characterName || (isTicketBlacklist ? 'N/A - Bloqueio de Tickets' : null);
                punishmentData.city = selectedCharacter?.city || null;
            }

            return {
                ...punishmentData,
                punishmentType: punishmentForm.type,
                publicReason: punishmentForm.reason,
                internalReason: punishmentForm.notes,
                appliedBy: staffName,
                expiresAt: calculateExpiration(punishmentForm.duration, punishmentForm.durationType, punishmentForm.type, punishmentForm.isIndefinite),
                userIP: targetUserData?.ip || null,
                userEmail: targetUserData?.email || null,
                userPhone: targetUserData?.phone || null,
                evidencePath: punishmentForm.evidencePath || null,
                attachments: punishmentForm.attachments || []
            };
        } catch (error) {
            console.error(error);
            toast.error("Erro ao preparar dados da punição.");
            return null; // Return null on error
        }
    };

    const submitPunishment = async (punishmentData) => {
        setIsApplyingPunishment(true);
        try {
            // executePunishment from Context
            await executePunishment(punishmentData);

            const isTicketBlacklist = punishmentData.punishmentType === 'Blacklist de Tickets';
            const successMessage = isTicketBlacklist
                ? 'Blacklist de Tickets aplicada com sucesso! O usuário não poderá criar novos tickets.'
                : 'Punição aplicada com sucesso no painel do site, agora você precisa aplicar a punição dentro da cidade. Obs: Lembre-se que aqui é apenas para registro e informar o usuário quando ele for buscar suporte sobre o status de cada perfil.';

            toast.success(successMessage, {
                duration: isTicketBlacklist ? 5000 : 1200000
            });

            // Reset Form (maintain manual mode if active for convenience, or reset? Let's reset relevant fields)
            setPunishmentForm(prev => ({
                ...prev,
                reason: '',
                notes: '',
                evidencePath: null,
                attachments: [],
                isManualMode: false,
                manualCity: 'CAPITAL',
                manualCharacterId: '',
                manualDiscordId: ''
            }));
            setSelectedUser(null);
            setSelectedCharacter(null);
            setPunishmentSearch('');

        } catch (error) {
            console.error("Error applying punishment:", error);
            toast.error("Erro ao aplicar punição: " + error.message);
        } finally {
            setIsApplyingPunishment(false);
        }
    };

    const confirmPunishmentWithoutProof = async (externalUrl) => {
        if (!pendingPunishment) return;

        let finalPayload = { ...pendingPunishment };

        if (externalUrl && externalUrl.length > 5) {
            finalPayload.notes = (finalPayload.notes || '') + '\n\n**[CONFIRMADO SEM ANEXO - LINK EXTERNO]**\n' + externalUrl;
            // Optionally store in a specific field if your schema supports it, otherwise notes is safest
        } else {
            finalPayload.notes = (finalPayload.notes || '') + '\n\n**[CONFIRMADO SEM ANEXO - OPÇÃO DO STAFF]**\n(Nenhuma prova anexada e nenhum link externo fornecido)';
        }

        setShowNoProofWarning(false);
        await submitPunishment(finalPayload);
        setPendingPunishment(null);
    };

    const handleApplyPunishment = async () => {
        const payload = await preparePunishmentPayload();
        if (!payload) return;

        // Check for evidence
        if (!payload.evidencePath) {
            setPendingPunishment(payload);
            setShowNoProofWarning(true);
            return;
        }

        await submitPunishment(payload);
    };

    const handleSearchHistory = () => {
        if (!managePunishmentSearch || managePunishmentSearch.length < 2) return;

        setIsSearchingHistory(true);
        setPunishmentProfiles([]);
        setSelectedHistoryProfile(null);
        setSearchedHistoryUser(null);

        // Simulate network delay for UX
        setTimeout(() => {
            const term = managePunishmentSearch.toLowerCase();
            const usersArray = Array.isArray(allUsers) ? allUsers : Object.values(allUsers || {});

            // Filter users based on criteria
            const foundProfiles = usersArray.filter(u => {
                const nameMatch = u.name?.toLowerCase().includes(term);
                const discordMatch = u.discordId?.toString().includes(term);
                const charNameMatch = u.cities?.some(c => c.characterName?.toLowerCase().includes(term));
                const charIdMatch = u.cities?.some(c => c.playerId?.toString().includes(term));

                return nameMatch || discordMatch || charNameMatch || charIdMatch;
            });

            setPunishmentProfiles(foundProfiles);
            setIsSearchingHistory(false);

            // Feedback if no results
            if (foundProfiles.length === 0) {
                toast.info("Nenhum perfil encontrado com esses dados.");
            }
        }, 600);
    };

    const handleSelectHistoryProfile = (userProfile) => {
        setSelectedHistoryProfile(userProfile);
        setSearchedHistoryUser(userProfile); // Keep sync for compatibility

        // Fetch punishments for this specific user
        const allPunishments = getAllPunishments();
        const userPunishments = allPunishments.filter(p => p.userId === userProfile.discordId);

        // Sort punishments
        const sortedPunishments = userPunishments.sort((a, b) => {
            const now = new Date();
            // Active first
            const isAActive = a.status === 'active' && (!a.expiresAt || new Date(a.expiresAt) > now);
            const isBActive = b.status === 'active' && (!b.expiresAt || new Date(b.expiresAt) > now);

            if (isAActive && !isBActive) return -1;
            if (!isAActive && isBActive) return 1;

            // Date descending
            return new Date(b.appliedAt || b.createdAt) - new Date(a.appliedAt || a.createdAt);
        });

        // Calculate counts
        const now = new Date();
        const activeCount = sortedPunishments.filter(a => a.status === 'active' && (!a.expiresAt || new Date(a.expiresAt) > now)).length;

        // Calculate summons for this user
        const userSummons = getUserTickets(userProfile.discordId).filter(t => t.isSummon || t.title?.startsWith('INTIMAÇÃO:'));

        setHistoryResults(sortedPunishments);
        setActivePunishmentsCount(activeCount);
        setSummonsCount(userSummons.length);
    };

    const handleOpenRevokeModal = (punishment) => {
        if (!['admin', 'head_staff'].includes(user?.accessLevel)) {
            toast.error("Apenas Head Staff e Admin podem revogar punições por aqui.");
            return;
        }
        setPunishmentToRevoke(punishment);
        setRevocationReason('');
        setIsRevokeModalOpen(true);
    };

    const handleConfirmRevocation = async () => {
        if (!revocationReason) {
            toast.error('Por favor, informe o motivo da revogação.');
            return;
        }

        try {
            await revokePunishment(punishmentToRevoke.id, staffName, revocationReason);

            toast.success('Punição revogada com sucesso!');
            setIsRevokeModalOpen(false);

            // Manually update local state for immediate feedback
            setHistoryResults(prev => {
                const now = new Date();
                const updatedPunishment = {
                    ...punishmentToRevoke,
                    status: 'revoked',
                    revokedBy: staffName,
                    revokedAt: new Date().toISOString(),
                    revocationReason
                };

                const newResults = prev.map(p => p.id === punishmentToRevoke.id ? updatedPunishment : p);

                // Recalculate active count
                const activeOnes = newResults.filter(a => a.status === 'active' && (!a.expiresAt || new Date(a.expiresAt) > now));
                setActivePunishmentsCount(activeOnes.length);

                return newResults;
            });

            setPunishmentToRevoke(null);
        } catch (error) {
            console.error("Error revoking punishment:", error);
            toast.error("Erro ao revogar punição.");
        }
    };

    const handleDeletePunishment = (id) => {
        if (!['admin', 'direcao'].includes(user?.accessLevel)) {
            toast.error('Você não tem permissão para esta ação.');
            return;
        }

        if (window.confirm('CUIDADO: Você está prestes a apagar esta punição permanentemente do banco de dados. Esta ação não pode ser desfeita. Deseja continuar?')) {
            deletePunishment(id);
            toast.success('Punição apagada permanentemente!');
            handleSearchHistory(); // Refresh list
        }
    };

    const handleOpenEvidence = async (url) => {
        if (!url) return;

        console.log('Opening evidence:', url);

        let fullUrl = url;
        let isR2Key = false; // Flag to track if we're using a direct R2 key

        // If it starts with 'evidence/', assume it's an R2 KEY from our new uploader
        if (url.startsWith('evidence/')) {
            isR2Key = true;
            // It's already the key, don't change fullUrl to Supabase
        } else if (!url.startsWith('http') && !url.startsWith('blob:')) {
            // Assuming 'punishments' bucket based on PunishmentContext usage
            const { data } = supabase.storage.from('punishments').getPublicUrl(url);
            fullUrl = data.publicUrl;
        }

        // Determine type
        const lowerUrl = fullUrl.toLowerCase();
        const isVideo = lowerUrl.endsWith('.mp4') || lowerUrl.endsWith('.webm') || lowerUrl.endsWith('.mov') || lowerUrl.endsWith('.mkv');
        const isMkv = lowerUrl.endsWith('.mkv');

        setEvidenceType(isVideo ? 'video' : 'image');
        setEvidenceModalOpen(true);
        setEvidenceLoading(true);
        setEvidenceUrl(null); // Reset current URL while loading
        setEvidenceFileKey(null);

        // If it's MKV, warn user
        if (isMkv) {
            toast('Arquivos .MKV não rodam nativamente no navegador. Use o botão de globo para abrir/baixar.', {
                icon: '⚠️'
            });
        }

        // Extract Object Key for R2 Secure Access
        try {
            let key = null;

            if (isR2Key) {
                key = url;
            } else {
                try {
                    const urlObj = new URL(fullUrl);
                    // Remove leading slash to get key
                    key = urlObj.pathname.substring(1);

                    // Supabase URL fix: invalid R2 key if it includes /storage/v1...
                    // Typical Supabase public URL: .../storage/v1/object/public/punishments/evidence/...
                    // We need just: evidence/...
                    if (key.includes('punishments/')) {
                        const parts = key.split('punishments/');
                        if (parts.length > 1) key = parts[1];
                    }
                } catch (e) {
                    // If not a valid URL (e.g. relative path), use it as key
                    key = fullUrl;
                }
            }

            console.log('Final Key for R2:', key);
            setEvidenceFileKey(key);

            // Fetch Secure Signed URL
            const response = await fetch('/api/r2-get-video', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ objectKey: key })
            });

            if (!response.ok) throw new Error('Failed to get secure URL');

            const data = await response.json();
            setEvidenceUrl(data.url);
        } catch (error) {
            console.error('Error fetching secure evidence URL:', error);
            // toast.error('Erro ao carregar prova segura. Tentando acesso direto...');
            if (!isR2Key && (fullUrl.startsWith('http') || fullUrl.startsWith('blob:'))) {
                setEvidenceUrl(fullUrl); // Fallback to public/direct URL if secure fails AND we have one
            } else {
                toast.error('Erro ao carregar prova segura.');
            }
        } finally {
            setEvidenceLoading(false);
        }
    };

    const handleDeleteSummon = async (ticketId) => {
        if (!window.confirm('Tem certeza que deseja excluir permanentemente esta intimação e o ticket associado?')) return;

        try {
            const res = await deleteTicket(ticketId);
            if (res.success) {
                toast.success('Inimação e ticket excluídos com sucesso.', {
                    icon: <Trash2 className="h-4 w-4 text-red-500" />
                });
            } else {
                toast.error('Erro ao excluir: ' + (res.error || 'Erro desconhecido'));
            }
        } catch (error) {
            console.error('Erro ao excluir intimação:', error);
            toast.error('Erro interno do servidor.');
        }
    };

    const handleSummonUser = async () => {
        if (!selectedSummonUser || !selectedSummonCharacter || !summonForm.reason || !summonForm.publicText) {
            toast.error('Preencha todos os campos obrigatórios.');
            return;
        }

        setIsSummoning(true);
        try {
            // 1. Criar o Ticket em nome do usuário
            const ticketData = {
                title: `INTIMAÇÃO: ${summonForm.reason}`,
                category: 'denuncia_jogador', // Categoria de moderação/denúncia
                description: `**[INTIMAÇÃO OFICIAL]**\n\n${summonForm.publicText}`,
                internalNotes: summonForm.internalText,
                userId: selectedSummonUser.discordId,
                userDiscordId: selectedSummonUser.discordId,
                authorName: selectedSummonUser.name,
                city: selectedSummonCharacter.city,
                playerId: selectedSummonCharacter.playerId,
                characterName: selectedSummonCharacter.characterName,
                status: 'aberto',
                auxiliaryStatus: ['intimacao'],
                isSummon: true
            };

            const res = await createTicket(ticketData);
            if (!res.success) throw new Error('Falha ao criar ticket');

            // 2. Assumir o ticket automaticamente (Atribuir ao staff logado)
            await claimTicket(res.ticketId);

            // 3. Enviar resposta interna com as notas staff se houver
            if (summonForm.internalText) {
                await addTicketResponse(res.ticketId, `**NOTAS DA INTIMAÇÃO (INTERNO):**\n${summonForm.internalText}`, true);
            }

            // 4. Criar o Anúncio Direcionado
            // 4. Criar o Anúncio Direcionado (Supabase)
            const announcement = {
                title: '🎯 Você possui uma Intimação Pendente',
                content: `Você foi intimado por ${staffName} para tratar de: ${summonForm.reason}. Por favor, acesse o ticket para mais informações.`,
                author: staffName,
                created_at: new Date().toISOString(),
                priority: 'high',
                visible: true,
                type: 'public', // Using 'public' but targeted via target_user_id
                target_user_id: selectedSummonUser.discordId,
                ticket_id: res.ticketId
            };

            const { error: announcementError } = await supabase
                .from('announcements')
                .insert([announcement]);

            if (announcementError) {
                console.error("Error creating summon announcement:", announcementError);
                // toast.error("Erro ao criar notificação da intimação."); // Optional warning
            }

            toast.success('Usuário intimado com sucesso! Ticket #' + res.ticketId + ' criado e anúncio enviado.');

            // Resetar formulário
            setSelectedSummonUser(null);
            setSelectedSummonCharacter(null);
            setSummonForm({ reason: '', publicText: '', internalText: '' });
            setSummonSearch('');
        } catch (error) {
            console.error(error);
            toast.error('Erro ao realizar intimação. Tente novamente.');
        } finally {
            setIsSummoning(false);
        }
    };

    // Mock Data Independente
    // ========== CENTRAL DE IDS (Supabase) ==========
    const [availableIds, setAvailableIds] = useState({});

    const fetchStaffIds = async () => {
        try {
            const { data, error } = await supabase
                .from('staff_ids')
                .select('*');

            if (error) {
                console.error("[StaffTools] Error fetching IDs:", error);
            } else {
                // Group by city for UI compatibility
                const grouped = (data || []).reduce((acc, current) => {
                    const city = current.city;
                    if (!acc[city]) acc[city] = [];
                    acc[city].push({
                        id: current.id,
                        status: current.status,
                        lastUsed: current.last_used,
                        reservedBy: current.reserved_by
                    });
                    return acc;
                }, {});

                // Ensure basic cities exist if empty
                const defaultCities = ['CAPITAL', 'VALLEY NORTE', 'VALLEY SUL', 'MORADA DO VALLEY'];
                defaultCities.forEach(c => {
                    if (!grouped[c]) grouped[c] = [];
                });

                setAvailableIds(grouped);
            }
        } catch (err) {
            console.error("[StaffTools] Unexpected error fetching IDs:", err);
        }
    };

    useEffect(() => {
        if (!user) return;

        fetchStaffIds();

        // Realtime subscription for IDs
        const channel = supabase.channel('staff-ids-realtime')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'staff_ids' }, fetchStaffIds)
            .subscribe();

        return () => supabase.removeChannel(channel);
    }, [user]);

    const handleCreateId = async () => {
        if (!newIdData.id || !newIdData.city) return;

        try {
            const { error } = await supabase
                .from('staff_ids')
                .insert([{
                    id: newIdData.id,
                    city: newIdData.city,
                    status: 'available'
                }]);

            if (error) throw error;

            toast.success(`ID ${newIdData.id} adicionado com sucesso à ${newIdData.city}.`);
            setIsCreatingId(false);
            setNewIdData({ id: '', city: 'CAPITAL' });
        } catch (error) {
            console.error(error);
            toast.error('Erro ao adicionar ID. Verifique se ele já existe.');
        }
    };

    const handleRefreshIds = async () => {
        setIsRefreshingIds(true);
        try {
            await fetchStaffIds();
            toast.success('IDs atualizados.');
        } catch (error) {
            console.error('Error refreshing IDs:', error);
            toast.error('Erro ao atualizar IDs.');
        } finally {
            setIsRefreshingIds(false);
        }
    };

    const handleReserveId = async (id, city) => {
        try {
            const { error } = await supabase
                .from('staff_ids')
                .update({
                    status: 'reserved',
                    reserved_by: staffName || 'Staff',
                    last_used: new Date().toISOString()
                })
                .eq('id', id);

            if (error) throw error;
            toast.success(`ID ${id} reservado.`);
        } catch (error) {
            console.error(error);
            toast.error('Erro ao reservar ID.');
        }
    };

    const handleReleaseId = async (id, city) => {
        try {
            const { error } = await supabase
                .from('staff_ids')
                .update({
                    status: 'available',
                    reserved_by: null
                })
                .eq('id', id);

            if (error) throw error;
            toast.info(`ID ${id} liberado.`);
        } catch (error) {
            console.error(error);
            toast.error('Erro ao liberar ID.');
        }
    };

    const handleDeleteId = async (id, city) => {
        if (!confirm('Tem certeza que deseja excluir este ID definitivamente?')) return;

        try {
            const { error } = await supabase
                .from('staff_ids')
                .delete()
                .eq('id', id);

            if (error) throw error;
            toast.success(`ID ${id} excluído.`);
        } catch (error) {
            console.error(error);
            toast.error('Erro ao excluir ID.');
        }
    };

    const handleCopyId = (id) => {
        navigator.clipboard.writeText(id);
    };

    const getFilteredIds = () => {
        const ids = availableIds[activeIdTab] || [];
        if (!searchId) return ids;
        return ids.filter(item => item.id.includes(searchId));
    };

    return (
        <div className="space-y-6 lg:space-y-8 p-2 sm:p-4 lg:p-6 min-h-screen bg-transparent">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white flex items-center tracking-tight">
                        <div className="p-2 sm:p-3 rounded-xl lg:rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 shadow-lg shadow-indigo-500/20 mr-3 lg:mr-4">
                            <Wrench className="h-5 w-5 sm:h-6 sm:w-6 lg:h-8 lg:w-8 text-white" />
                        </div>
                        <span className="bg-gradient-to-r from-white via-indigo-100 to-purple-100 bg-clip-text text-transparent">
                            Ferramenta Staff
                        </span>
                    </h1>
                    <p className="text-gray-400 mt-2 text-sm lg:text-lg ml-1 hidden sm:block">
                        Central de configurações e recursos administrativos avançados
                    </p>
                </div>
            </div>

            {/* Main Content Area */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
                <TabsList className="bg-black/20 backdrop-blur-xl border border-white/5 p-1 sm:p-1.5 h-auto w-full justify-start rounded-xl lg:rounded-2xl gap-1 sm:gap-2 flex-wrap shadow-xl overflow-x-auto">
                    {!isLegalDept && (
                        <>
                            <TabsTrigger
                                value="faq-inteligente"
                                className="flex-1 min-w-[100px] sm:min-w-[140px] lg:min-w-[180px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-600 data-[state=active]:to-purple-600 data-[state=active]:text-white text-gray-400 hover:text-white hover:bg-white/5 transition-all duration-300 py-2 sm:py-3 rounded-lg lg:rounded-xl border border-transparent data-[state=active]:shadow-lg data-[state=active]:shadow-indigo-500/25"
                            >
                                <div className="flex items-center justify-center gap-1 sm:gap-2.5">
                                    <Sparkles className="h-4 w-4 lg:h-5 lg:w-5" />
                                    <span className="font-semibold tracking-wide text-xs sm:text-sm">FAQ</span>
                                </div>
                            </TabsTrigger>

                            <TabsTrigger
                                value="dosimetria"
                                className="flex-1 min-w-[100px] sm:min-w-[140px] lg:min-w-[180px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-600 data-[state=active]:to-teal-600 data-[state=active]:text-white text-gray-400 hover:text-white hover:bg-white/5 transition-all duration-300 py-2 sm:py-3 rounded-lg lg:rounded-xl border border-transparent data-[state=active]:shadow-lg data-[state=active]:shadow-emerald-500/25"
                            >
                                <div className="flex items-center justify-center gap-1 sm:gap-2.5">
                                    <Scale className="h-4 w-4 lg:h-5 lg:w-5" />
                                    <span className="font-semibold tracking-wide text-xs sm:text-sm">Dosimetria</span>
                                </div>
                            </TabsTrigger>
                            <TabsTrigger
                                value="response-patterns"
                                className="flex-1 min-w-[100px] sm:min-w-[140px] lg:min-w-[180px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-indigo-600 data-[state=active]:text-white text-gray-400 hover:text-white hover:bg-white/5 transition-all duration-300 py-2 sm:py-3 rounded-lg lg:rounded-xl border border-transparent data-[state=active]:shadow-lg data-[state=active]:shadow-blue-500/25"
                            >
                                <div className="flex items-center justify-center gap-1 sm:gap-2.5">
                                    <MessageSquare className="h-4 w-4 lg:h-5 lg:w-5" />
                                    <span className="font-semibold tracking-wide text-xs sm:text-sm">Padrões</span>
                                </div>
                            </TabsTrigger>
                        </>
                    )}
                    <TabsTrigger
                        value="verification"
                        className="flex-1 min-w-[100px] sm:min-w-[140px] lg:min-w-[180px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-600 data-[state=active]:to-teal-600 data-[state=active]:text-white text-gray-400 hover:text-white hover:bg-white/5 transition-all duration-300 py-2 sm:py-3 rounded-lg lg:rounded-xl border border-transparent data-[state=active]:shadow-lg data-[state=active]:shadow-emerald-500/25"
                    >
                        <div className="flex items-center justify-center gap-1 sm:gap-2.5">
                            <Search className="h-4 w-4 lg:h-5 lg:w-5" />
                            <span className="font-semibold tracking-wide text-xs sm:text-sm">Verificação</span>
                        </div>
                    </TabsTrigger>
                    {!isLegalDept && (
                        <>
                            <TabsTrigger
                                value="available-ids"
                                className="flex-1 min-w-[180px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-600 data-[state=active]:to-teal-600 data-[state=active]:text-white text-gray-400 hover:text-white hover:bg-white/5 transition-all duration-300 py-3 rounded-xl border border-transparent data-[state=active]:shadow-lg data-[state=active]:shadow-emerald-500/25"
                            >
                                <div className="flex items-center justify-center gap-2.5">
                                    <Hash className="h-5 w-5" />
                                    <span className="font-semibold tracking-wide">Central de IDs</span>
                                </div>
                            </TabsTrigger>
                            <TabsTrigger
                                value="punishments"
                                className="flex-1 min-w-[180px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-600 data-[state=active]:to-rose-600 data-[state=active]:text-white text-gray-400 hover:text-white hover:bg-white/5 transition-all duration-300 py-3 rounded-xl border border-transparent data-[state=active]:shadow-lg data-[state=active]:shadow-red-500/25"
                            >
                                <div className="flex items-center justify-center gap-2.5">
                                    <Gavel className="h-5 w-5" />
                                    <span className="font-semibold tracking-wide">Aplicar Punição</span>
                                </div>
                            </TabsTrigger>
                            <TabsTrigger
                                value="manage-punishments"
                                className="flex-1 min-w-[180px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-600 data-[state=active]:to-amber-600 data-[state=active]:text-white text-gray-400 hover:text-white hover:bg-white/5 transition-all duration-300 py-3 rounded-xl border border-transparent data-[state=active]:shadow-lg data-[state=active]:shadow-orange-500/25"
                            >
                                <div className="flex items-center justify-center gap-2.5">
                                    <History className="h-5 w-5" />
                                    <span className="font-semibold tracking-wide">Gerenciar Punições</span>
                                </div>
                            </TabsTrigger>
                            <TabsTrigger
                                value="summon-user"
                                className="flex-1 min-w-[180px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-500 data-[state=active]:text-white text-gray-400 hover:text-white hover:bg-white/5 transition-all duration-300 py-3 rounded-xl border border-transparent data-[state=active]:shadow-lg data-[state=active]:shadow-amber-500/25"
                            >
                                <div className="flex items-center justify-center gap-2.5">
                                    <Megaphone className="h-5 w-5" />
                                    <span className="font-semibold tracking-wide">Intimar Usuário</span>
                                </div>
                            </TabsTrigger>
                            <TabsTrigger
                                value="player-lookup"
                                className="flex-1 min-w-[180px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-indigo-600 data-[state=active]:text-white text-gray-400 hover:text-white hover:bg-white/5 transition-all duration-300 py-3 rounded-xl border border-transparent data-[state=active]:shadow-lg data-[state=active]:shadow-blue-500/25"
                            >
                                <div className="flex items-center justify-center gap-2.5">
                                    <Search className="h-5 w-5" />
                                    <span className="font-semibold tracking-wide">Buscar Jogador</span>
                                </div>
                            </TabsTrigger>
                        </>
                    )}
                </TabsList>


                <AnimatePresence mode="wait">
                    {activeTab === 'response-patterns' && (
                        <motion.div
                            key="response-patterns"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <TabsContent value="response-patterns" className="mt-0">
                                <StaffResponsePatterns />
                            </TabsContent>
                        </motion.div>
                    )}
                    {activeTab === 'verification' && (
                        <motion.div
                            key="verification"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <TabsContent value="verification" className="mt-0">
                                <StaffTicketVerification />
                            </TabsContent>
                        </motion.div>
                    )}
                    {activeTab === 'faq-inteligente' && (
                        <motion.div
                            key="faq-inteligente"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <TabsContent value="faq-inteligente" className="mt-0">
                                <Card className="bg-black/40 backdrop-blur-xl border-white/10 shadow-2xl overflow-hidden">
                                    <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-[100px] -z-10" />
                                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/10 rounded-full blur-[100px] -z-10" />

                                    <CardHeader className="border-b border-white/5 pb-6">
                                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                            <div>
                                                <CardTitle className="text-2xl font-bold text-white flex items-center gap-3">
                                                    <Hash className="h-6 w-6 text-indigo-400" />
                                                    Gerenciamento de Respostas
                                                </CardTitle>
                                                <CardDescription className="text-gray-400 mt-1">
                                                    Configure as respostas automáticas do assistente virtual
                                                </CardDescription>
                                            </div>
                                            <Button
                                                onClick={() => {
                                                    setSelectedFAQ(null);
                                                    setFaqForm({ question: '', answer: '', category: 'suporte_duvidas' });
                                                    setIsEditingFAQ(true);
                                                }}
                                                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg shadow-indigo-500/25 border-0 rounded-xl px-6 h-11 transition-all hover:scale-105 active:scale-95"
                                            >
                                                <Plus className="h-5 w-5 mr-2" />
                                                Adicionar FAQ
                                            </Button>
                                        </div>
                                    </CardHeader>

                                    <CardContent className="p-6 space-y-6">
                                        {/* Search Bar */}
                                        <div className="relative group">
                                            <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                            <div className="relative">
                                                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-indigo-400 transition-colors" />
                                                <Input
                                                    placeholder="Pesquisar por perguntas ou respostas..."
                                                    value={faqSearch}
                                                    onChange={(e) => setFaqSearch(e.target.value)}
                                                    className="pl-12 bg-black/50 border-white/10 text-white h-12 rounded-xl focus:border-indigo-500/50 focus:ring-indigo-500/20 transition-all placeholder:text-gray-600"
                                                />
                                            </div>
                                        </div>

                                        {/* Grid de Cards */}
                                        <div className="grid grid-cols-1 gap-4">
                                            {filteredFaqs.map((faq, idx) => {
                                                const style = getCategoryStyle(faq.category);
                                                const CategoryIcon = style.icon;

                                                return (
                                                    <motion.div
                                                        key={faq.id}
                                                        initial={{ opacity: 0, scale: 0.98 }}
                                                        animate={{ opacity: 1, scale: 1 }}
                                                        transition={{ delay: idx * 0.05 }}
                                                        className="group relative bg-white/5 hover:bg-white/[0.07] border border-white/5 hover:border-indigo-500/30 rounded-xl p-5 transition-all duration-300"
                                                    >
                                                        <div className="flex flex-col md:flex-row md:items-start gap-5">
                                                            {/* Icon Box */}
                                                            <div className={`p-3 rounded-xl ${style.bg} border ${style.border} shrink-0`}>
                                                                <CategoryIcon className={`h-6 w-6 ${style.color}`} />
                                                            </div>

                                                            {/* Content */}
                                                            <div className="flex-1 min-w-0 space-y-2">
                                                                <div className="flex items-center gap-3 mb-1 flex-wrap">
                                                                    <h3 className="text-lg font-semibold text-white group-hover:text-indigo-300 transition-colors">
                                                                        {faq.question}
                                                                    </h3>
                                                                    <Badge variant="outline" className={`${style.bg} ${style.color} ${style.border} border`}>
                                                                        {style.label}
                                                                    </Badge>
                                                                </div>
                                                                <p className="text-gray-400 text-sm leading-relaxed border-l-2 border-white/10 pl-3">
                                                                    {faq.answer}
                                                                </p>
                                                            </div>

                                                            {/* Actions */}
                                                            <div className="flex flex-row md:flex-col gap-2 shrink-0 opacity-100 md:opacity-0 group-hover:opacity-100 transition-all ml-auto md:ml-0">
                                                                <Button
                                                                    size="icon"
                                                                    variant="ghost"
                                                                    onClick={() => handleEditFAQ(faq)}
                                                                    className="h-8 w-8 text-gray-400 hover:text-indigo-400 hover:bg-indigo-500/10 rounded-lg"
                                                                >
                                                                    <Edit className="h-4 w-4" />
                                                                </Button>
                                                                <Button
                                                                    size="icon"
                                                                    variant="ghost"
                                                                    onClick={() => handleDeleteFAQ(faq.id)}
                                                                    className="h-8 w-8 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg"
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                );
                                            })}

                                            {filteredFaqs.length === 0 && (
                                                <div className="flex flex-col items-center justify-center py-16 text-center">
                                                    <div className="bg-gray-800/50 p-6 rounded-full mb-4">
                                                        <Search className="h-10 w-10 text-gray-600" />
                                                    </div>
                                                    <h3 className="text-xl font-bold text-white mb-2">Nenhum resultado encontrado</h3>
                                                    <p className="text-gray-500 max-w-md">
                                                        Tente buscar por outros termos ou adicione um novo item ao FAQ.
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            </TabsContent>
                        </motion.div>
                    )}

                    {activeTab === 'dosimetria' && (
                        <motion.div
                            key="dosimetria"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <TabsContent value="dosimetria" className="mt-0">
                                <StaffDosimetry />
                            </TabsContent>
                        </motion.div>
                    )}

                    {activeTab === 'available-ids' && (
                        <motion.div
                            key="available-ids"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <TabsContent value="available-ids" className="mt-0">
                                <Card className="bg-black/40 backdrop-blur-xl border-white/10 shadow-2xl overflow-hidden">
                                    <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-[100px] -z-10" />
                                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-teal-500/10 rounded-full blur-[100px] -z-10" />

                                    <CardHeader className="border-b border-white/5 pb-6">
                                        {/* Header do ID */}
                                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                            <div>
                                                <CardTitle className="text-2xl font-bold text-white flex items-center gap-3">
                                                    <Hash className="h-6 w-6 text-emerald-400" />
                                                    IDs Disponíveis
                                                </CardTitle>
                                                <CardDescription className="text-gray-400 mt-1">
                                                    Consulte e reserve IDs para novos jogadores
                                                </CardDescription>
                                            </div>
                                            <div className="flex gap-3">
                                                <Button
                                                    variant="outline"
                                                    onClick={handleRefreshIds}
                                                    className={`border-white/10 text-gray-300 hover:bg-white/5 hover:text-white rounded-xl h-11 px-4 bg-transparent ${isRefreshingIds ? 'animate-pulse' : ''}`}
                                                >
                                                    <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshingIds ? 'animate-spin' : ''}`} />
                                                    {isRefreshingIds ? 'Buscando...' : 'Atualizar'}
                                                </Button>
                                                <Button
                                                    onClick={() => setIsCreatingId(true)}
                                                    className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/25 border-0 rounded-xl px-6 h-11 transition-all hover:scale-105 active:scale-95"
                                                >
                                                    <Plus className="h-5 w-5 mr-2" />
                                                    Novo ID
                                                </Button>
                                            </div>
                                        </div>

                                        {/* City Selector */}
                                        <div className="mt-8 flex gap-2 overflow-x-auto pb-2 scrollbar-none">
                                            {Object.keys(availableIds).map(city => (
                                                <button
                                                    key={city}
                                                    onClick={() => setActiveIdTab(city)}
                                                    className={`px-5 py-2.5 rounded-xl font-medium text-sm transition-all whitespace-nowrap ${activeIdTab === city
                                                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 shadow-lg shadow-emerald-500/10'
                                                        : 'bg-white/5 text-gray-400 border border-white/5 hover:bg-white/10 hover:text-white'
                                                        }`}
                                                >
                                                    {city}
                                                </button>
                                            ))}
                                        </div>
                                    </CardHeader>

                                    <CardContent className="p-6 space-y-6">
                                        {/* Search */}
                                        <div className="relative group max-w-md">
                                            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                            <div className="relative">
                                                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-emerald-400 transition-colors" />
                                                <Input
                                                    placeholder="Buscar ID..."
                                                    value={searchId}
                                                    onChange={(e) => setSearchId(e.target.value)}
                                                    className="pl-12 bg-black/50 border-white/10 text-white h-12 rounded-xl focus:border-emerald-500/50 focus:ring-emerald-500/20 transition-all placeholder:text-gray-600"
                                                />
                                            </div>
                                        </div>

                                        {/* ID Grid */}
                                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                            {getFilteredIds().map((item, idx) => (
                                                <motion.div
                                                    key={item.id}
                                                    initial={{ opacity: 0, scale: 0.9 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    transition={{ delay: idx * 0.05 }}
                                                    className={`relative group p-4 rounded-xl border transition-all duration-300 flex flex-col items-center justify-center gap-3 backdrop-blur-sm ${item.status === 'available'
                                                        ? 'bg-emerald-500/5 border-emerald-500/20 hover:border-emerald-500/40 hover:bg-emerald-500/10 hover:shadow-lg hover:shadow-emerald-500/10'
                                                        : 'bg-orange-500/5 border-orange-500/20 opacity-75'
                                                        }`}
                                                >
                                                    <div className={`text-2xl font-black tracking-widest ${item.status === 'available' ? 'text-white group-hover:text-emerald-300' : 'text-orange-300/50'
                                                        }`}>
                                                        {item.id}
                                                    </div>

                                                    <Badge className={`${item.status === 'available'
                                                        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                                                        : 'bg-orange-500/20 text-orange-400 border-orange-500/30'
                                                        } text-[10px] uppercase tracking-wider`}>
                                                        {item.status === 'available' ? 'Disponível' : 'Reservado'}
                                                    </Badge>

                                                    {item.status === 'available' ? (
                                                        <div className="w-full flex gap-2 mt-1 opacity-0 group-hover:opacity-100 transition-opacity translate-y-2 group-hover:translate-y-0">
                                                            <Button
                                                                size="sm"
                                                                onClick={() => handleCopyId(item.id)}
                                                                className="flex-1 h-8 bg-white/5 hover:bg-white/20 border-0 rounded-lg"
                                                            >
                                                                <Hash className="h-3 w-3" />
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                onClick={() => handleReserveId(item.id, activeIdTab)}
                                                                className="flex-[2] h-8 bg-emerald-600 hover:bg-emerald-500 text-white border-0 rounded-lg text-xs"
                                                            >
                                                                Reservar
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <div className="w-full flex gap-2 mt-1 opacity-0 group-hover:opacity-100 transition-opacity translate-y-2 group-hover:translate-y-0">

                                                            <Button
                                                                size="sm"
                                                                onClick={() => handleReleaseId(item.id, activeIdTab)}
                                                                variant="outline"
                                                                className="flex-1 h-8 border-orange-500/30 text-orange-400 hover:bg-orange-500/10 hover:text-orange-300 rounded-lg text-xs"
                                                                title="Liberar ID (Remover Reserva)"
                                                            >
                                                                Liberar
                                                            </Button>

                                                            {['admin', 'head_staff', 'direcao'].includes(user?.accessLevel) && (
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => handleDeleteId(item.id, activeIdTab)}
                                                                    className="h-8 w-8 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded-lg p-0"
                                                                    title="Excluir ID Definitivamente"
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    )}

                                                    {item.status !== 'available' && (
                                                        <div className="text-[10px] text-gray-500 text-center mt-1 group-hover:hidden">
                                                            por {item.reservedBy}
                                                        </div>
                                                    )}
                                                </motion.div>
                                            ))}
                                            {getFilteredIds().length === 0 && (
                                                <div className="col-span-full py-12 flex flex-col items-center justify-center text-gray-500 border border-dashed border-white/10 rounded-xl bg-white/5">
                                                    <Hash className="h-12 w-12 mb-3 opacity-20" />
                                                    <p>Nenhum ID encontrado</p>
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            </TabsContent>
                        </motion.div>
                    )}

                    {activeTab === 'punishments' && (
                        <motion.div
                            key="punishments"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <TabsContent value="punishments" className="mt-0">
                                <Card className="bg-black/40 backdrop-blur-xl border-white/10 shadow-2xl overflow-hidden">
                                    <div className="absolute top-0 right-0 w-64 h-64 bg-red-500/10 rounded-full blur-[100px] -z-10" />
                                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-rose-500/10 rounded-full blur-[100px] -z-10" />

                                    <CardHeader className="border-b border-white/5 pb-6">
                                        <CardTitle className="text-2xl font-bold text-white flex items-center gap-3">
                                            <Gavel className="h-6 w-6 text-red-500" />
                                            Aplicação de Punições
                                        </CardTitle>
                                        <CardDescription className="text-gray-400 mt-1">
                                            Busque um usuário e aplique a punição correspondente
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="p-6 space-y-6">
                                        {/* SEARCH SECTION */}
                                        {!selectedUser && !punishmentForm.isManualMode ? (
                                            <div className="space-y-4">
                                                {/* Punishment Type Selector - Show early for Blacklist de Tickets */}
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium text-gray-300">Tipo de Punição</label>
                                                    <Select
                                                        value={punishmentForm.type}
                                                        onValueChange={(val) => setPunishmentForm({ ...punishmentForm, type: val })}
                                                    >
                                                        <SelectTrigger className="bg-black/40 border-white/10 text-white h-11 rounded-xl">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent className="bg-[#1a1d24] border-white/10 text-white">
                                                            {PUNISHMENT_TYPES.map(type => (
                                                                <SelectItem key={type} value={type}>{type}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>

                                                {/* Direct Discord ID Input for Blacklist de Tickets */}
                                                {punishmentForm.type === 'Blacklist de Tickets' && (
                                                    <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 space-y-4">
                                                        <div className="flex items-center gap-3 text-red-400">
                                                            <ShieldAlert className="h-5 w-5" />
                                                            <span className="font-bold text-sm uppercase tracking-wider">Blacklist de Tickets - Entrada Direta</span>
                                                        </div>
                                                        <p className="text-sm text-gray-400">
                                                            Insira o Discord ID diretamente para aplicar a blacklist de tickets,
                                                            ou busque um usuário cadastrado abaixo.
                                                        </p>
                                                        <div className="relative group">
                                                            <div className="relative">
                                                                <Hash className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-red-400 transition-colors" />
                                                                <Input
                                                                    placeholder="Discord ID (ex: 123456789012345678)"
                                                                    value={punishmentForm.directDiscordId}
                                                                    onChange={(e) => setPunishmentForm({ ...punishmentForm, directDiscordId: e.target.value })}
                                                                    className="pl-12 bg-black/50 border-white/10 text-white h-12 rounded-xl focus:border-red-500/50 focus:ring-red-500/20 transition-all placeholder:text-gray-600 font-mono"
                                                                />
                                                            </div>
                                                        </div>

                                                        {punishmentForm.directDiscordId && (
                                                            <div className="space-y-4 pt-4 border-t border-red-500/20">
                                                                <div className="space-y-2">
                                                                    <label className="text-sm font-medium text-gray-300">Motivo (Público) <span className="text-red-500">*</span></label>
                                                                    <Input
                                                                        value={punishmentForm.reason}
                                                                        onChange={(e) => setPunishmentForm({ ...punishmentForm, reason: e.target.value })}
                                                                        className="bg-black/40 border-white/10 text-white h-11 rounded-xl"
                                                                        placeholder="Ex: Abuso do sistema de suporte..."
                                                                    />
                                                                </div>

                                                                <div className="space-y-2">
                                                                    <label className="text-sm font-medium text-gray-300">Notas Internas <span className="text-red-500">*</span></label>
                                                                    <Textarea
                                                                        value={punishmentForm.notes}
                                                                        onChange={(e) => setPunishmentForm({ ...punishmentForm, notes: e.target.value })}
                                                                        className="bg-black/40 border-white/10 text-white min-h-[100px] rounded-xl resize-none p-4"
                                                                        placeholder="Detalhes visíveis apenas para a staff..."
                                                                    />
                                                                </div>

                                                                <Button
                                                                    onClick={handleApplyPunishment}
                                                                    disabled={isApplyingPunishment || !punishmentForm.reason || !punishmentForm.notes}
                                                                    className="w-full h-12 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-bold shadow-lg shadow-red-900/20 rounded-xl"
                                                                >
                                                                    {isApplyingPunishment ? (
                                                                        <>
                                                                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                                                            Aplicando...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <ShieldAlert className="h-5 w-5 mr-2" />
                                                                            Aplicar Blacklist de Tickets
                                                                        </>
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* SEARCH INPUT */}


                                                <div className="relative group">
                                                    <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 to-rose-500/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                                    <div className="relative">
                                                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-red-400 transition-colors" />
                                                        <Input
                                                            placeholder="Buscar por Nome, Discord ID ou ID do Personagem..."
                                                            value={punishmentSearch}
                                                            onChange={(e) => setPunishmentSearch(e.target.value)}
                                                            className="pl-12 bg-black/50 border-white/10 text-white h-12 rounded-xl focus:border-red-500/50 focus:ring-red-500/20 transition-all placeholder:text-gray-600"
                                                        />
                                                    </div>
                                                </div>

                                                {/* SEARCH RESULTS */}
                                                {punishmentSearch.length >= 2 && !punishmentForm.isManualMode && (
                                                    <div className="grid gap-2">
                                                        {getFilteredUsers().map(user => (
                                                            <div
                                                                key={user.discordId}
                                                                onClick={() => handleSelectUser(user)}
                                                                className="flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 hover:border-red-500/30 cursor-pointer transition-all group"
                                                            >
                                                                <div className="flex items-center gap-4">
                                                                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-gray-700 to-gray-600 flex items-center justify-center overflow-hidden shrink-0">
                                                                        {user.avatar ? (
                                                                            <img src={user.avatar.startsWith('http') ? user.avatar : `https://cdn.discordapp.com/avatars/${user.discordId}/${user.avatar}.png?size=128`} alt={user.name} className="h-full w-full object-cover" />
                                                                        ) : (
                                                                            <span className="text-lg font-bold text-gray-300">{user.name?.charAt(0)}</span>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex flex-col gap-1.5">
                                                                        <div>
                                                                            <div className="font-medium text-white group-hover:text-red-300 transition-colors">{user.name}</div>
                                                                            <div className="text-xs text-gray-400">Discord: {user.discordId}</div>
                                                                        </div>

                                                                        {user.cities && user.cities.length > 0 && (
                                                                            <div className="flex flex-wrap gap-2">
                                                                                {user.cities.map((city, idx) => (
                                                                                    <div key={idx} className="flex items-center text-xs bg-black/40 px-2 py-1 rounded-md border border-white/5 text-gray-300">
                                                                                        <span className="font-mono font-bold text-red-400 mr-1.5">#{city.playerId}</span>
                                                                                        <span>{city.characterName}</span>
                                                                                        {city.city && <span className="text-gray-500 ml-1.5 border-l border-white/10 pl-1.5 uppercase text-[10px]">{city.city}</span>}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <ChevronRight className="h-4 w-4 text-gray-500 group-hover:text-red-400 transition-colors" />
                                                            </div>
                                                        ))}
                                                        {getFilteredUsers().length === 0 && punishmentSearch.length >= 2 && (
                                                            <div className="text-center py-8 text-gray-500 bg-white/5 rounded-xl border border-white/5 flex flex-col items-center gap-4">
                                                                <p>Nenhum usuário encontrado com esses dados.</p>
                                                                <Button
                                                                    onClick={() => setPunishmentForm(prev => ({ ...prev, isManualMode: true }))}
                                                                    className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20"
                                                                >
                                                                    <User className="w-4 h-4 mr-2" />
                                                                    Punir Usuário Não Registrado (Pré-Registro)
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            /* USER SELECTED OR MANUAL MODE */
                                            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
                                                {/* HEADER: USER CARD OR MANUAL NOTICE */}
                                                {!punishmentForm.isManualMode ? (
                                                    <div className="flex flex-col gap-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-4">
                                                                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center shadow-lg shadow-red-500/20 overflow-hidden">
                                                                    {selectedUser.avatar ? (
                                                                        <img
                                                                            src={selectedUser.avatar.startsWith('http') ? selectedUser.avatar : `https://cdn.discordapp.com/avatars/${selectedUser.discordId}/${selectedUser.avatar}.png?size=128`}
                                                                            alt={selectedUser.name}
                                                                            className="h-full w-full object-cover"
                                                                        />
                                                                    ) : (
                                                                        <span className="text-xl font-bold text-white">{selectedUser.name?.charAt(0)}</span>
                                                                    )}
                                                                </div>
                                                                <div>
                                                                    <h3 className="text-lg font-bold text-white">{selectedUser.name}</h3>
                                                                    <div className="text-sm text-red-300">
                                                                        <span>Discord: {selectedUser.discordId}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => setSelectedUser(null)}
                                                                className="text-gray-400 hover:text-white hover:bg-white/10"
                                                            >
                                                                <X className="h-5 w-5" />
                                                            </Button>
                                                        </div>

                                                        {/* CHARACTER SELECTION - Only show for non-ticket-blacklist punishments */}
                                                        {punishmentForm.type !== 'Blacklist de Tickets' ? (
                                                            <div className="pt-4 border-t border-red-500/20">
                                                                <label className="text-sm font-bold text-red-400 block mb-3 uppercase tracking-wider">Selecione o Perfil para Punir <span className="text-red-500">*</span></label>
                                                                <div className="grid gap-3">
                                                                    {selectedUser.cities && selectedUser.cities.length > 0 ? (
                                                                        selectedUser.cities.map((city, idx) => (
                                                                            <div
                                                                                key={idx}
                                                                                onClick={() => setSelectedCharacter(city)}
                                                                                className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all border ${selectedCharacter && String(selectedCharacter.playerId) === String(city.playerId) && normalizeStr(selectedCharacter.city) === normalizeStr(city.city)
                                                                                    ? 'bg-red-500/20 border-red-500 shadow-lg shadow-red-500/10'
                                                                                    : 'bg-black/40 border-white/5 hover:border-red-500/30'
                                                                                    }`}
                                                                            >
                                                                                <div className="flex items-center gap-3">
                                                                                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center font-bold font-mono text-sm ${selectedCharacter && String(selectedCharacter.playerId) === String(city.playerId) && normalizeStr(selectedCharacter.city) === normalizeStr(city.city) ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-400'
                                                                                        }`}>
                                                                                        #{city.playerId || '?'}
                                                                                    </div>
                                                                                    <div>
                                                                                        <div className="font-bold text-white">{city.characterName}</div>
                                                                                        <div className="text-[10px] text-gray-500 uppercase">{city.city}</div>
                                                                                    </div>
                                                                                </div>
                                                                                {selectedCharacter && String(selectedCharacter.playerId) === String(city.playerId) && normalizeStr(selectedCharacter.city) === normalizeStr(city.city) && (
                                                                                    <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                                                                                )}
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        <div className="text-sm text-gray-500 italic p-3 bg-black/20 rounded-xl border border-dashed border-white/5">
                                                                            Nenhum personagem vinculado encontrado para este usuário.
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="pt-4 border-t border-red-500/20">
                                                                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                                                                    <div className="flex items-center gap-3 text-red-400 mb-2">
                                                                        <ShieldAlert className="h-5 w-5" />
                                                                        <span className="font-bold text-sm uppercase tracking-wider">Blacklist de Tickets</span>
                                                                    </div>
                                                                    <p className="text-sm text-gray-400">
                                                                        Esta punição será aplicada diretamente à conta Discord do usuário selecionado.
                                                                        O usuário não poderá criar novos tickets no sistema de suporte.
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    // MANUAL DATA ENTRY
                                                    <div className="flex flex-col gap-4 p-4 bg-orange-500/10 border border-orange-500/20 rounded-xl">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div className="flex items-center gap-2 text-orange-400">
                                                                <User className="h-5 w-5" />
                                                                <span className="font-bold uppercase tracking-wider">Punição Manual (Pré-Registro)</span>
                                                            </div>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => setPunishmentForm(prev => ({ ...prev, isManualMode: false }))}
                                                                className="text-gray-400 hover:text-white hover:bg-white/10"
                                                            >
                                                                <X className="h-5 w-5" />
                                                            </Button>
                                                        </div>

                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                            <div className="space-y-2">
                                                                <label className="text-sm font-medium text-gray-300">Discord ID <span className="text-red-500">*</span></label>
                                                                <Input
                                                                    value={punishmentForm.manualDiscordId}
                                                                    onChange={(e) => setPunishmentForm({ ...punishmentForm, manualDiscordId: e.target.value })}
                                                                    className="bg-black/40 border-white/10 text-white"
                                                                    placeholder="Ex: 123456789..."
                                                                />
                                                            </div>
                                                            <div className="space-y-2">
                                                                <label className="text-sm font-medium text-gray-300">Cidade <span className="text-red-500">*</span></label>
                                                                <Select
                                                                    value={punishmentForm.manualCity}
                                                                    onValueChange={(val) => setPunishmentForm({ ...punishmentForm, manualCity: val })}
                                                                >
                                                                    <SelectTrigger className="bg-black/40 border-white/10 text-white">
                                                                        <SelectValue />
                                                                    </SelectTrigger>
                                                                    <SelectContent className="bg-[#1a1d24] border-white/10 text-white">
                                                                        <SelectItem value="CAPITAL">Capital</SelectItem>
                                                                        <SelectItem value="VALLEY NORTE">Valley Norte</SelectItem>
                                                                        <SelectItem value="MORADA DO VALLEY">Morada do Valley</SelectItem>
                                                                        <SelectItem value="VALLEY SUL">Valley Sul</SelectItem>
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>
                                                        </div>

                                                        <div className="space-y-2">
                                                            <label className="text-sm font-medium text-gray-300">ID do Personagem <span className="text-red-500">*</span></label>
                                                            <Input
                                                                value={punishmentForm.manualCharacterId}
                                                                onChange={(e) => setPunishmentForm({ ...punishmentForm, manualCharacterId: e.target.value })}
                                                                className="bg-black/40 border-white/10 text-white"
                                                                placeholder="Ex: 123"
                                                            />
                                                        </div>

                                                        <div className="p-3 bg-orange-500/5 rounded-lg border border-orange-500/10 text-sm text-orange-200/70">
                                                            <p>ℹ️ Esta punição será salva como "Usuário Desconhecido". Quando o usuário se registrar com este Discord ID, o sistema vinculará automaticamente a punição ao perfil dele.</p>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* PUNISHMENT FORM */}
                                                <div className="grid gap-6">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                        <div className="space-y-2">
                                                            <label className="text-sm font-medium text-gray-300">Tipo de Punição</label>
                                                            <Select
                                                                value={punishmentForm.type}
                                                                onValueChange={(val) => setPunishmentForm({ ...punishmentForm, type: val })}
                                                            >
                                                                <SelectTrigger className="bg-black/40 border-white/10 text-white h-11 rounded-xl">
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent className="bg-[#1a1d24] border-white/10 text-white">
                                                                    {PUNISHMENT_TYPES.map(type => (
                                                                        <SelectItem key={type} value={type}>{type}</SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>

                                                        {/* DURATION INPUTS */}
                                                        <div className="flex flex-col gap-2">
                                                            <div className="flex gap-2">
                                                                <div className="space-y-2 flex-1">
                                                                    <label className="text-sm font-medium text-gray-300">Duração</label>
                                                                    <Input
                                                                        type="number"
                                                                        disabled={punishmentForm.isIndefinite}
                                                                        value={punishmentForm.duration}
                                                                        onChange={(e) => setPunishmentForm({ ...punishmentForm, duration: e.target.value })}
                                                                        className="bg-black/40 border-white/10 text-white h-11 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                                                                        placeholder={punishmentForm.isIndefinite ? "Indeterminado" : "0"}
                                                                    />
                                                                </div>
                                                                <div className="space-y-2 w-32">
                                                                    <label className="text-sm font-medium text-gray-300">Unidade</label>
                                                                    <Select
                                                                        disabled={punishmentForm.isIndefinite}
                                                                        value={punishmentForm.durationType}
                                                                        onValueChange={(val) => setPunishmentForm({ ...punishmentForm, durationType: val })}
                                                                    >
                                                                        <SelectTrigger className="bg-black/40 border-white/10 text-white h-11 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed">
                                                                            <SelectValue />
                                                                        </SelectTrigger>
                                                                        <SelectContent className="bg-[#1a1d24] border-white/10 text-white">
                                                                            <SelectItem value="days">Dias</SelectItem>
                                                                            <SelectItem value="hours">Horas</SelectItem>
                                                                        </SelectContent>
                                                                    </Select>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center space-x-2 pt-1">
                                                                <Checkbox
                                                                    id="indefinite"
                                                                    checked={punishmentForm.isIndefinite}
                                                                    onCheckedChange={(checked) => setPunishmentForm({ ...punishmentForm, isIndefinite: checked, duration: checked ? '' : punishmentForm.duration })}
                                                                    className="border-white/20 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
                                                                />
                                                                <label
                                                                    htmlFor="indefinite"
                                                                    className="text-sm font-medium text-gray-400 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer select-none hover:text-white transition-colors"
                                                                >
                                                                    Tempo Indeterminado
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="space-y-2">
                                                        <label className="text-sm font-medium text-gray-300">Motivo (Público) <span className="text-red-500">*</span></label>
                                                        <Input
                                                            value={punishmentForm.reason}
                                                            onChange={(e) => setPunishmentForm({ ...punishmentForm, reason: e.target.value })}
                                                            className="bg-black/40 border-white/10 text-white h-11 rounded-xl"
                                                            placeholder="Ex: Anti-RP, VDM, RDM..."
                                                        />
                                                    </div>

                                                    <div className="space-y-2">
                                                        <label className="text-sm font-medium text-gray-300">Notas Internas <span className="text-red-500">*</span></label>
                                                        <Textarea
                                                            value={punishmentForm.notes}
                                                            onChange={(e) => setPunishmentForm({ ...punishmentForm, notes: e.target.value })}
                                                            className="bg-black/40 border-white/10 text-white min-h-[100px] rounded-xl resize-none p-4"
                                                            placeholder="Detalhes visíveis apenas para a staff..."
                                                        />
                                                    </div>

                                                    <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                                                        <label className="text-sm font-bold text-gray-400 mb-3 block flex items-center gap-2">
                                                            <FileVideo className="w-4 h-4 text-blue-400" />
                                                            Anexar Prova (Vídeo/Arquivo)
                                                        </label>
                                                        <R2Upload
                                                            disabled={!isPunishmentFormValid}
                                                            folderPath={`evidence/${new Date().getFullYear()}/${String(new Date().getMonth() + 1).padStart(2, '0')}/${String(new Date().getDate()).padStart(2, '0')}/${(selectedCharacter?.city || punishmentForm.manualCity || 'GERAL').normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase()}/${selectedUser?.discordId || punishmentForm.manualDiscordId || punishmentForm.directDiscordId || 'unknown'}/${selectedCharacter?.playerId || punishmentForm.manualCharacterId || 'NA'}/${punishmentForm.type.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, '_').toLowerCase()}`}
                                                            onUploadComplete={(paths) => {
                                                                if (!paths || (Array.isArray(paths) && paths.length === 0)) {
                                                                    return;
                                                                }

                                                                const newPaths = Array.isArray(paths) ? paths : [paths];
                                                                const pathsString = newPaths.join('\nRef: ');

                                                                console.log("Upload Complete. Paths:", newPaths); // Debug

                                                                // Use functional update to ensure we have latest state
                                                                setPunishmentForm(prev => {
                                                                    const updatedNotes = prev.notes ?
                                                                        prev.notes + `\n\n**[PROVA(S) ANEXADA(S) (R2)]**\nRef: ${pathsString}` :
                                                                        `**[PROVA(S) ANEXADA(S) (R2)]**\nRef: ${pathsString}`;

                                                                    return {
                                                                        ...prev,
                                                                        notes: updatedNotes,
                                                                        evidencePath: newPaths[0], // Store primary proof for quick access/reference
                                                                        attachments: newPaths // Store all paths
                                                                    };
                                                                });

                                                                toast.success("Provas anexadas e vinculadas ao formulário!");
                                                            }}
                                                        />
                                                    </div>

                                                    <Button
                                                        onClick={handleApplyPunishment}
                                                        disabled={isApplyingPunishment}
                                                        className="w-full h-12 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-bold shadow-lg shadow-red-900/20 rounded-xl mt-2"
                                                    >
                                                        {isApplyingPunishment ? (
                                                            <>
                                                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                                                Aplicando...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Gavel className="h-5 w-5 mr-2" />
                                                                Confirmar Aplicação de Punição
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </CardContent>


                                    {/* NO PROOF WARNING DIALOG */}
                                    <AlertDialog open={showNoProofWarning} onOpenChange={setShowNoProofWarning}>
                                        <AlertDialogContent className="bg-zinc-900 border border-red-500/30 text-white max-w-lg">
                                            <AlertDialogHeader>
                                                <AlertDialogTitle className="text-xl font-bold flex items-center gap-2">
                                                    {!punishmentForm.externalProofUrl ? (
                                                        <>
                                                            <ShieldAlert className="h-6 w-6 text-red-500" />
                                                            <span className="text-red-500">Atenção: Nenhuma Prova Anexada</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Link className="h-6 w-6 text-blue-500" />
                                                            <span>Confirmar com Link Externo</span>
                                                        </>
                                                    )}
                                                </AlertDialogTitle>
                                                <AlertDialogDescription className="text-gray-300 space-y-4 pt-2">
                                                    <p>
                                                        Você não anexou nenhum arquivo de prova.
                                                        Para garantir a segurança jurídica da punição, escolha uma das opções abaixo:
                                                    </p>

                                                    {!punishmentForm.externalProofUrl && (
                                                        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-200 animate-in fade-in">
                                                            <p className="font-bold flex items-center gap-2 mb-1">
                                                                <AlertTriangle className="h-4 w-4" />
                                                                AVISO JURÍDICO E DE AUDITORIA
                                                            </p>
                                                            <p>
                                                                A aplicação de punições sem provas documentadas (upload ou link) expõe a administração a riscos de contestações sem defesa.
                                                                Ao prosseguir, você assume a responsabilidade por esta ação.
                                                            </p>
                                                        </div>
                                                    )}

                                                    <div className="space-y-3 pt-2">
                                                        <div className="space-y-1">
                                                            <label className="text-xs font-bold text-gray-400 uppercase">
                                                                Opção 1: Link Externo (Opcional)
                                                            </label>
                                                            <Input
                                                                placeholder="https://youtube.com/watch?v=..."
                                                                value={punishmentForm.externalProofUrl || ''}
                                                                onChange={(e) => setPunishmentForm({ ...punishmentForm, externalProofUrl: e.target.value })}
                                                                className="bg-black/40 border-white/10 text-white"
                                                            />
                                                            <p className="text-[10px] text-gray-500">
                                                                YouTube, Medal, Streamable, etc.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter className="sm:justify-between">
                                                <AlertDialogCancel className="bg-white/5 border-white/10 hover:bg-white/10 text-white">
                                                    Cancelar
                                                </AlertDialogCancel>
                                                <AlertDialogAction
                                                    onClick={() => confirmPunishmentWithoutProof(punishmentForm.externalProofUrl)}
                                                    className={`border-0 w-full sm:w-auto ${punishmentForm.externalProofUrl ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'}`}
                                                >
                                                    {punishmentForm.externalProofUrl ? (
                                                        <span className="font-bold">Confirmar com Link</span>
                                                    ) : (
                                                        <span className="flex flex-col items-start text-left">
                                                            <span className="font-bold">Confirmar Sem Provas</span>
                                                            <span className="text-[10px] opacity-80 font-normal">Estou ciente dos riscos</span>
                                                        </span>
                                                    )}
                                                </AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>

                                </Card>
                            </TabsContent>
                        </motion.div>
                    )}
                    {activeTab === 'manage-punishments' && (
                        <motion.div
                            key="manage-punishments"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <TabsContent value="manage-punishments" className="mt-0">
                                <Card className="bg-black/40 backdrop-blur-xl border-white/10 shadow-2xl overflow-hidden">
                                    <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-[100px] -z-10" />
                                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-amber-500/10 rounded-full blur-[100px] -z-10" />

                                    <CardHeader className="border-b border-white/5 pb-6">
                                        <CardTitle className="text-2xl font-bold text-white flex items-center gap-3">
                                            <History className="h-6 w-6 text-orange-500" />
                                            Gerenciar Histórico de Punições
                                        </CardTitle>
                                        <CardDescription className="text-gray-400 mt-1">
                                            Pesquise e gerencie punições por Discord ID ou ID do Personagem
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="p-6 space-y-6">
                                        {/* SEARCH SECTION */}
                                        <div className="flex gap-3">
                                            <div className="relative group flex-1">
                                                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-amber-500/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                                <div className="relative">
                                                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-orange-400 transition-colors" />
                                                    <Input
                                                        placeholder="Discord ID, ID do Personagem ou Nome..."
                                                        value={managePunishmentSearch}
                                                        onChange={(e) => setManagePunishmentSearch(e.target.value)}
                                                        onKeyDown={(e) => e.key === 'Enter' && handleSearchHistory()}
                                                        className="pl-12 bg-black/50 border-white/10 text-white h-12 rounded-xl focus:border-orange-500/50 focus:ring-orange-500/20 transition-all placeholder:text-gray-600"
                                                    />
                                                </div>
                                            </div>
                                            <Button
                                                onClick={handleSearchHistory}
                                                disabled={isSearchingHistory || !managePunishmentSearch}
                                                className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white shadow-lg shadow-orange-500/20 border-0 rounded-xl px-8 h-12 transition-all"
                                            >
                                                {isSearchingHistory ? (
                                                    <RefreshCw className="h-5 w-5 animate-spin" />
                                                ) : (
                                                    "Pesquisar"
                                                )}
                                            </Button>
                                        </div>

                                        {/* PROFILE RESULTS GRID */}
                                        {!selectedHistoryProfile && punishmentProfiles.length > 0 && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-in fade-in slide-in-from-bottom-4">
                                                {punishmentProfiles.map((profile) => (
                                                    <div
                                                        key={profile.discordId}
                                                        onClick={() => handleSelectHistoryProfile(profile)}
                                                        className="group bg-white/5 hover:bg-white/10 border border-white/5 hover:border-orange-500/30 rounded-xl p-4 cursor-pointer transition-all duration-300 relative overflow-hidden"
                                                    >
                                                        <div className="flex items-center gap-4 mb-3">
                                                            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center overflow-hidden border border-white/10 group-hover:border-orange-500/50 transition-colors">
                                                                {profile.avatar ? (
                                                                    <img src={profile.avatar.startsWith('http') ? profile.avatar : `https://cdn.discordapp.com/avatars/${profile.discordId}/${profile.avatar}.png?size=128`} alt={profile.name} className="h-full w-full object-cover" />
                                                                ) : (
                                                                    <span className="text-sm font-bold text-indigo-400">{profile.name?.charAt(0)}</span>
                                                                )}
                                                            </div>
                                                            <div>
                                                                <h4 className="font-bold text-white group-hover:text-orange-400 transition-colors">{profile.name}</h4>
                                                                <div className="flex items-center gap-1.5 text-xs text-gray-500 font-mono">
                                                                    <Hash className="h-3 w-3" />
                                                                    {profile.discordId}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {profile.cities && profile.cities.length > 0 ? (
                                                            <div className="space-y-2">
                                                                <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wider">Personagens</div>
                                                                <div className="flex flex-wrap gap-2">
                                                                    {profile.cities.map((city, idx) => (
                                                                        <Badge key={idx} variant="secondary" className="bg-black/40 text-gray-300 border-white/5">
                                                                            #{city.playerId} {city.characterName}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="text-xs text-gray-500 italic mt-2">Nenhum personagem vinculado</div>
                                                        )}

                                                        <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-600 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* DETAILED HISTORY VIEW */}
                                        {selectedHistoryProfile && (
                                            <div className="space-y-6 animate-in fade-in slide-in-from-right-8">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setSelectedHistoryProfile(null)}
                                                    className="text-gray-400 hover:text-white -ml-2 mb-2"
                                                >
                                                    <ChevronRight className="h-4 w-4 mr-1 rotate-180" />
                                                    Voltar para resultados
                                                </Button>

                                                {/* USER PROFILE SUMMARY - REUSING EXISTING LAYOUT */}
                                                <div className="flex flex-col md:flex-row items-start md:items-center justify-between p-6 bg-white/5 rounded-2xl border border-white/10 gap-6">
                                                    <div className="flex items-center gap-5">
                                                        <div className="h-16 w-16 rounded-full bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center overflow-hidden border-2 border-indigo-500/30">
                                                            {searchedHistoryUser.avatar ? (
                                                                <img src={searchedHistoryUser.avatar.startsWith('http') ? searchedHistoryUser.avatar : `https://cdn.discordapp.com/avatars/${searchedHistoryUser.discordId}/${searchedHistoryUser.avatar}.png?size=128`} alt={searchedHistoryUser.name} className="h-full w-full object-cover" />
                                                            ) : (
                                                                <span className="text-2xl font-bold text-indigo-400">{searchedHistoryUser.name?.charAt(0)}</span>
                                                            )}
                                                        </div>
                                                        <div className="space-y-1">
                                                            <div className="text-2xl font-black text-white flex items-center gap-2">
                                                                {searchedHistoryUser.name}
                                                                {activePunishmentsCount > 0 && (
                                                                    <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[10px] uppercase font-bold animate-pulse">
                                                                        Possui Punições Ativas
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                            <div className="flex flex-wrap gap-3">
                                                                <div className="text-xs text-gray-400 bg-white/5 px-2.5 py-1 rounded-lg border border-white/5 flex items-center gap-1.5 font-mono">
                                                                    <Hash className="h-3 w-3" />
                                                                    {searchedHistoryUser.discordId}
                                                                </div>
                                                                {searchedHistoryUser.cities?.map((city, idx) => (
                                                                    <div key={idx} className="text-xs text-indigo-400 bg-indigo-500/5 px-2.5 py-1 rounded-lg border border-indigo-500/10 flex items-center gap-1.5 font-bold">
                                                                        <User className="h-3 w-3" />
                                                                        #{city.playerId} {city.characterName}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-col items-end gap-2">
                                                        {activePunishmentsCount === 0 ? (
                                                            <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 py-1.5 px-4 text-sm font-bold">
                                                                O usuário não possui nenhuma punição ativa no momento.
                                                            </Badge>
                                                        ) : (
                                                            <Badge className="bg-red-500/10 text-red-400 border-red-500/20 py-1.5 px-4 text-sm font-bold">
                                                                {activePunishmentsCount} {activePunishmentsCount === 1 ? 'Punição Ativa' : 'Punições Ativas'}
                                                            </Badge>
                                                        )}
                                                        {summonsCount > 0 && (
                                                            <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 py-1.5 px-4 text-sm font-bold animate-bounce mt-1">
                                                                <ShieldAlert className="h-4 w-4 mr-2" />
                                                                Foi encontradas {summonsCount} intimações pendentes.
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* DETAILED HISTORY */}
                                                {historyResults.length > 0 ? (
                                                    <div className="space-y-4">
                                                        <div className="flex items-center gap-2 text-indigo-400 mb-2">
                                                            <History className="h-5 w-5" />
                                                            <h3 className="font-bold uppercase tracking-widest text-xs">Histórico Completo de Ocorrências</h3>
                                                        </div>
                                                        <div className="grid gap-6">
                                                            {historyResults.map((p, idx) => {
                                                                const isRevoked = p.status === 'revoked';
                                                                const isExpired = p.expiresAt && new Date(p.expiresAt) < new Date();
                                                                const isActive = !isRevoked && !isExpired;

                                                                return (
                                                                    <motion.div
                                                                        key={p.id}
                                                                        initial={{ opacity: 0, x: -20 }}
                                                                        animate={{ opacity: 1, x: 0 }}
                                                                        transition={{ delay: idx * 0.1 }}
                                                                        className={`relative group bg-white/5 rounded-2xl border ${isRevoked ? 'border-gray-500/20' : 'border-white/10'} hover:border-orange-500/30 p-6 transition-all duration-300`}
                                                                    >
                                                                        <div className="absolute top-6 right-6 flex gap-2">
                                                                            {isRevoked ? (
                                                                                <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">Revogada</Badge>
                                                                            ) : isExpired ? (
                                                                                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Expirada</Badge>
                                                                            ) : (
                                                                                <Badge className="bg-red-500/20 text-red-100 border-red-500/30 animate-pulse">Ativa</Badge>
                                                                            )}
                                                                        </div>

                                                                        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                                                                            <div className="md:col-span-4 space-y-4 border-r border-white/5 pr-6 flex flex-col">
                                                                                <div>
                                                                                    <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1">Tipo de Punição</label>
                                                                                    <p className="text-xl font-black text-white">{p.punishmentType}</p>
                                                                                </div>
                                                                                <div className="grid grid-cols-2 gap-4">
                                                                                    <div>
                                                                                        <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1">Duração</label>
                                                                                        <div className="flex flex-col">
                                                                                            <span className="text-sm text-gray-300 font-medium">
                                                                                                {p.expiresAt ? "Temporária" : "Permanente"}
                                                                                            </span>
                                                                                            {p.expiresAt && (
                                                                                                <span className="text-[10px] text-orange-400 font-mono">
                                                                                                    Até {new Date(p.expiresAt).toLocaleDateString('pt-BR')} às {new Date(p.expiresAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                    <div>
                                                                                        <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1">Aplicação</label>
                                                                                        <div className="flex flex-col">
                                                                                            <span className="text-sm text-gray-300">
                                                                                                {new Date(p.appliedAt).toLocaleDateString('pt-BR')}
                                                                                            </span>
                                                                                            <span className="text-[10px] text-gray-500">
                                                                                                às {new Date(p.appliedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                                                                            </span>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                                <div className="bg-white/5 backdrop-blur-md rounded-xl p-3 border border-white/10 mt-auto">
                                                                                    <div className="flex items-center justify-between mb-2 pb-2 border-b border-white/5">
                                                                                        <div className="flex flex-col">
                                                                                            <span className="text-[10px] text-gray-500 font-bold uppercase">Cidade</span>
                                                                                            <span className="text-sm text-indigo-400 font-black uppercase tracking-tight">{p.city || 'CAPITAL'}</span>
                                                                                        </div>
                                                                                        <div className="h-8 w-px bg-white/5 mx-2" />
                                                                                        <div className="flex flex-col items-end">
                                                                                            <span className="text-[10px] text-gray-500 font-bold uppercase">Personagem</span>
                                                                                            <span className="text-sm text-white font-bold">{p.characterName || 'Desconhecido'}</span>
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="flex items-center justify-between">
                                                                                        <span className="text-[10px] text-gray-500 font-bold uppercase">Identificador</span>
                                                                                        <Badge variant="outline" className="text-[10px] font-mono border-orange-500/30 text-orange-400 bg-orange-500/5">
                                                                                            #{p.characterId || '?'}
                                                                                        </Badge>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                            <div className="md:col-span-5 space-y-4">
                                                                                <div className="flex items-center justify-between">
                                                                                    <div className="flex items-center gap-2">
                                                                                        <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Responsável:</label>
                                                                                        <Badge variant="outline" className="text-[10px] bg-indigo-500/10 text-indigo-400 border-indigo-500/20 py-0 h-5">
                                                                                            {p.appliedBy}
                                                                                        </Badge>
                                                                                    </div>
                                                                                    {p.whitelistAffected && (
                                                                                        <Badge variant="destructive" className="text-[10px] uppercase font-bold bg-red-500/10 text-red-400 border-red-500/20">
                                                                                            Whitelist Removida
                                                                                        </Badge>
                                                                                    )}
                                                                                </div>
                                                                                <div>
                                                                                    <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1">Motivo Público</label>
                                                                                    <div className="bg-black/20 p-3 rounded-xl border border-white/5">
                                                                                        <p className="text-sm text-gray-200 italic">"{p.publicReason}"</p>
                                                                                    </div>
                                                                                </div>
                                                                                <div>
                                                                                    <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1">Notas Internas & Provas</label>
                                                                                    <div className="bg-red-500/5 p-3 rounded-xl border border-red-500/10">
                                                                                        <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">{p.internalReason}</p>

                                                                                        {(p.attachments && p.attachments.length > 0) && (
                                                                                            <div className="mt-3 pt-3 border-t border-red-500/10 space-y-2">
                                                                                                {p.attachments.map((attachment, aIdx) => (
                                                                                                    <Button
                                                                                                        key={aIdx}
                                                                                                        variant="outline"
                                                                                                        size="sm"
                                                                                                        className="h-8 text-xs bg-black/20 border-white/10 hover:bg-white/5 hover:text-white w-full justify-start text-gray-400 hover:border-orange-500/30"
                                                                                                        onClick={() => handleOpenEvidence(attachment)}
                                                                                                    >
                                                                                                        {attachment.toLowerCase().endsWith('.mp4') || attachment.toLowerCase().endsWith('.webm') || attachment.toLowerCase().endsWith('.mov') || attachment.toLowerCase().endsWith('.mkv') ? (
                                                                                                            <FileVideo className="h-3 w-3 mr-2 text-indigo-400" />
                                                                                                        ) : (
                                                                                                            <FileImage className="h-3 w-3 mr-2 text-indigo-400" />
                                                                                                        )}
                                                                                                        Visualizar Prova Anexada #{aIdx + 1}
                                                                                                    </Button>
                                                                                                ))}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                            <div className="md:col-span-3 flex flex-col justify-end gap-3">
                                                                                {isActive && ['admin', 'head_staff'].includes(user?.accessLevel) && (
                                                                                    <Button
                                                                                        onClick={() => handleOpenRevokeModal(p)}
                                                                                        className="w-full bg-red-600/10 hover:bg-red-600 text-red-500 hover:text-white border border-red-500/20 hover:border-red-600 transition-all rounded-xl h-11"
                                                                                    >
                                                                                        <Trash2 className="h-4 w-4 mr-2" />
                                                                                        Revogar Punição
                                                                                    </Button>
                                                                                )}

                                                                                {isRevoked && (
                                                                                    <div className="bg-gray-800/40 p-3 rounded-xl border border-white/5 space-y-2">
                                                                                        <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block">Histórico de Revogação</label>
                                                                                        <p className="text-xs text-green-400 font-medium flex items-center gap-2">
                                                                                            <Wrench className="h-3 w-3" />
                                                                                            Por: {p.revokedBy}
                                                                                        </p>
                                                                                        <p className="text-[10px] text-gray-500">{new Date(p.revokedAt).toLocaleString('pt-BR')}</p>
                                                                                        <div className="bg-black/20 p-2 rounded-lg text-[11px] text-gray-400">
                                                                                            {p.revocationReason}
                                                                                        </div>
                                                                                    </div>
                                                                                )}

                                                                                {['admin', 'direcao'].includes(user?.accessLevel) && (
                                                                                    <Button
                                                                                        onClick={() => handleDeletePunishment(p.id)}
                                                                                        variant="ghost"
                                                                                        className="w-full text-red-500 hover:text-red-400 hover:bg-red-500/10 rounded-xl h-10 border border-transparent hover:border-red-500/20 text-xs mt-2"
                                                                                    >
                                                                                        <Trash2 className="h-3 w-3 mr-2" />
                                                                                        Apagar Histórico permanentemente
                                                                                    </Button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </motion.div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-center justify-center py-20 text-center bg-white/5 rounded-3xl border border-dashed border-white/10">
                                                        <div className="bg-indigo-500/10 p-6 rounded-full mb-4">
                                                            <CheckCircle2 className="h-12 w-12 text-indigo-500/50" />
                                                        </div>
                                                        <h3 className="text-xl font-bold text-white mb-2">Ficha Limpa</h3>
                                                        <p className="text-gray-500 max-w-sm">
                                                            O usuário não possui nenhum histórico de punições no banco de dados.
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* EMPTY STATE */}
                                        {!selectedHistoryProfile && punishmentProfiles.length === 0 && managePunishmentSearch && !isSearchingHistory && (
                                            <div className="flex flex-col items-center justify-center py-20 text-center bg-white/5 rounded-3xl border border-dashed border-white/10">
                                                <div className="bg-orange-500/10 p-6 rounded-full mb-4">
                                                    <SearchX className="h-12 w-12 text-orange-500/50" />
                                                </div>
                                                <h3 className="text-xl font-bold text-white mb-2">Nenhuma ocorrência encontrada</h3>
                                                <p className="text-gray-500 max-w-sm">
                                                    Não encontramos punições vinculadas a "{managePunishmentSearch}". Verifique o ID e tente novamente.
                                                </p>
                                            </div>
                                        )}

                                        {!selectedHistoryProfile && punishmentProfiles.length === 0 && !managePunishmentSearch && (
                                            <div className="flex flex-col items-center justify-center py-24 text-center">
                                                <div className="bg-white/5 p-8 rounded-full mb-6 border border-white/5">
                                                    <History className="h-12 w-12 text-gray-600" />
                                                </div>
                                                <h3 className="text-2xl font-black text-white mb-3">Histórico Criminal</h3>
                                                <p className="text-gray-500 max-w-md text-lg">
                                                    Digite um identificador acima para visualizar o histórico completo de punições do usuário.
                                                </p>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </TabsContent>
                        </motion.div>
                    )}
                    {activeTab === 'summon-user' && (
                        <motion.div
                            key="summon-user"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <TabsContent value="summon-user" className="mt-0">
                                <Card className="bg-black/40 backdrop-blur-xl border-white/10 shadow-2xl overflow-hidden">
                                    <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-[100px] -z-10" />
                                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange-500/10 rounded-full blur-[100px] -z-10" />

                                    <CardHeader className="border-b border-white/5 pb-6">
                                        <CardTitle className="text-2xl font-bold text-white flex items-center gap-3">
                                            <Megaphone className="h-6 w-6 text-amber-500" />
                                            Intimar Usuário
                                        </CardTitle>
                                        <CardDescription className="text-gray-400 mt-1">
                                            Crie uma intimação oficial para um usuário, gerando um ticket automático e anúncio direcionado.
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="p-6 space-y-6">
                                        {/* SEARCH SECTION */}
                                        {!selectedSummonUser ? (
                                            <div className="space-y-4">
                                                <div className="relative group">
                                                    <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                                    <div className="relative">
                                                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-amber-400 transition-colors" />
                                                        <Input
                                                            placeholder="Buscar por Nome, Discord ID..."
                                                            value={summonSearch}
                                                            onChange={(e) => setSummonSearch(e.target.value)}
                                                            className="pl-12 bg-black/50 border-white/10 text-white h-12 rounded-xl focus:border-amber-500/50 focus:ring-amber-500/20 transition-all placeholder:text-gray-600"
                                                        />
                                                    </div>
                                                </div>

                                                {/* SEARCH RESULTS */}
                                                {summonSearch.length >= 2 && (
                                                    <div className="grid gap-2">
                                                        {(Array.isArray(allUsers) ? allUsers : Object.values(allUsers || {}))
                                                            .filter(u => {
                                                                const term = summonSearch.toLowerCase();
                                                                const nameMatch = u.name?.toLowerCase().includes(term);
                                                                const discordMatch = u.discordId?.toString().includes(term);
                                                                const charNameMatch = u.cities?.some(c => c.characterName?.toLowerCase().includes(term));
                                                                const charIdMatch = u.cities?.some(c => c.playerId?.toString().includes(term));

                                                                return nameMatch || discordMatch || charNameMatch || charIdMatch;
                                                            })
                                                            .slice(0, 5)
                                                            .map(user => (
                                                                <div
                                                                    key={user.discordId}
                                                                    onClick={() => {
                                                                        setSelectedSummonUser(user);
                                                                        setSelectedSummonCharacter(null);
                                                                        setSummonSearch('');
                                                                    }}
                                                                    className="flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 hover:border-amber-500/30 cursor-pointer transition-all group"
                                                                >
                                                                    <div className="flex items-center gap-4">
                                                                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-gray-700 to-gray-600 flex items-center justify-center overflow-hidden shrink-0">
                                                                            {user.avatar ? (
                                                                                <img src={user.avatar.startsWith('http') ? user.avatar : `https://cdn.discordapp.com/avatars/${user.discordId}/${user.avatar}.png?size=128`} alt={user.name} className="h-full w-full object-cover" />
                                                                            ) : (
                                                                                <span className="text-lg font-bold text-gray-300">{user.name?.charAt(0)}</span>
                                                                            )}
                                                                        </div>
                                                                        <div className="space-y-1">
                                                                            <div className="font-medium text-white group-hover:text-amber-300 transition-colors">{user.name}</div>
                                                                            <div className="flex flex-wrap gap-2">
                                                                                <div className="text-[10px] text-gray-400 bg-white/5 px-2 py-0.5 rounded border border-white/5 uppercase font-bold tracking-tighter">
                                                                                    Discord: {user.discordId}
                                                                                </div>
                                                                                {user.cities && user.cities.length > 0 ? (
                                                                                    user.cities.map((city, cIdx) => (
                                                                                        <div key={cIdx} className="flex items-center gap-1.5 text-[10px] text-amber-500/70 bg-amber-500/5 px-2 py-0.5 rounded border border-amber-500/10 uppercase font-black tracking-tighter">
                                                                                            <span className="opacity-50">#{city.playerId}</span>
                                                                                            <span>{city.characterName}</span>
                                                                                            <span className="text-amber-600/50">•</span>
                                                                                            <span className="opacity-70">{city.city}</span>
                                                                                        </div>
                                                                                    ))
                                                                                ) : (
                                                                                    <div className="text-[10px] text-gray-600 italic px-2 py-0.5">Sem personagens vinculados</div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <ChevronRight className="h-5 w-5 text-gray-600 group-hover:text-amber-500 transition-all" />
                                                                </div>
                                                            ))}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="space-y-6">
                                                {/* SELECTED USER HEADER */}
                                                <div className="flex items-center justify-between p-4 bg-amber-500/10 rounded-2xl border border-amber-500/20">
                                                    <div className="flex items-center gap-4">
                                                        <div className="h-12 w-12 rounded-full bg-amber-500/20 flex items-center justify-center overflow-hidden border-2 border-amber-500/30">
                                                            {selectedSummonUser.avatar ? (
                                                                <img src={selectedSummonUser.avatar.startsWith('http') ? selectedSummonUser.avatar : `https://cdn.discordapp.com/avatars/${selectedSummonUser.discordId}/${selectedSummonUser.avatar}.png?size=128`} alt={selectedSummonUser.name} className="h-full w-full object-cover" />
                                                            ) : (
                                                                <span className="text-xl font-bold text-amber-500">{selectedSummonUser.name?.charAt(0)}</span>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <div className="text-lg font-bold text-white">{selectedSummonUser.name}</div>
                                                            <div className="text-sm text-amber-500/80 font-mono italic">@{selectedSummonUser.discordId}</div>
                                                        </div>
                                                    </div>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => setSelectedSummonUser(null)}
                                                        className="text-gray-400 hover:text-white hover:bg-white/5 rounded-xl h-9"
                                                    >
                                                        <X className="h-4 w-4 mr-2" />
                                                        Trocar Usuário
                                                    </Button>
                                                </div>

                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                                    {/* LEFT SIDE: CHARACTER SELECTION */}
                                                    <div className="space-y-4">
                                                        <label className="text-sm font-bold text-amber-400 block uppercase tracking-wider">Selecione o Perfil <span className="text-amber-500">*</span></label>
                                                        <div className="grid gap-3">
                                                            {selectedSummonUser.cities && selectedSummonUser.cities.length > 0 ? (
                                                                selectedSummonUser.cities.map((city, idx) => (
                                                                    <div
                                                                        key={idx}
                                                                        onClick={() => setSelectedSummonCharacter(city)}
                                                                        className={`flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all border ${selectedSummonCharacter && String(selectedSummonCharacter.playerId) === String(city.playerId) && selectedSummonCharacter.city === city.city
                                                                            ? 'bg-amber-500/20 border-amber-500 shadow-lg shadow-amber-500/10'
                                                                            : 'bg-black/40 border-white/5 hover:border-amber-500/30'
                                                                            }`}
                                                                    >
                                                                        <div className="flex items-center gap-3">
                                                                            <div className={`h-10 w-10 rounded-lg flex items-center justify-center font-bold font-mono text-sm ${selectedSummonCharacter && String(selectedSummonCharacter.playerId) === String(city.playerId) && selectedSummonCharacter.city === city.city ? 'bg-amber-500 text-white' : 'bg-white/5 text-gray-400'
                                                                                }`}>
                                                                                #{city.playerId}
                                                                            </div>
                                                                            <div>
                                                                                <div className="font-bold text-white text-base">{city.characterName}</div>
                                                                                <div className="text-xs text-gray-500 uppercase">{city.city}</div>
                                                                            </div>
                                                                        </div>
                                                                        {selectedSummonCharacter &&
                                                                            String(selectedSummonCharacter.playerId) === String(city.playerId) &&
                                                                            String(selectedSummonCharacter.city).trim().toLowerCase() === String(city.city).trim().toLowerCase() && (
                                                                                <CheckCircle2 className="h-5 w-5 text-amber-500" />
                                                                            )}
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div className="text-sm text-gray-500 italic p-6 bg-black/20 rounded-xl border border-dashed border-white/5 text-center">
                                                                    Nenhum personagem vinculado encontrado para este usuário.
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* RIGHT SIDE: SUMMON FORM */}
                                                    <div className="space-y-6">
                                                        <div className="space-y-2">
                                                            <label className="text-sm font-medium text-gray-300">Motivo da Intimação <span className="text-amber-500">*</span></label>
                                                            <Input
                                                                value={summonForm.reason}
                                                                onChange={(e) => setSummonForm({ ...summonForm, reason: e.target.value })}
                                                                className="bg-black/40 border-white/10 text-white h-11 rounded-xl"
                                                                placeholder="Ex: Comparecer ao suporte para esclarecimentos..."
                                                            />
                                                        </div>

                                                        <div className="space-y-2">
                                                            <label className="text-sm font-medium text-gray-300">Texto Público (Exibido no Ticket e Anúncio) <span className="text-amber-500">*</span></label>
                                                            <Textarea
                                                                value={summonForm.publicText}
                                                                onChange={(e) => setSummonForm({ ...summonForm, publicText: e.target.value })}
                                                                className="bg-black/40 border-white/10 text-white min-h-[100px] rounded-xl resize-none p-4"
                                                                placeholder="Descreva o que o usuário precisa fazer..."
                                                            />
                                                        </div>

                                                        <div className="space-y-2">
                                                            <label className="text-sm font-medium text-gray-300">Notas Internas (Apenas Staff)</label>
                                                            <Textarea
                                                                value={summonForm.internalText}
                                                                onChange={(e) => setSummonForm({ ...summonForm, internalText: e.target.value })}
                                                                className="bg-black/40 border-white/10 text-white min-h-[100px] rounded-xl resize-none p-4"
                                                                placeholder="Notas adicionais para a equipe..."
                                                            />
                                                        </div>

                                                        <Button
                                                            onClick={handleSummonUser}
                                                            disabled={isSummoning || !selectedSummonCharacter || !summonForm.reason || !summonForm.publicText}
                                                            className="w-full h-12 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-bold shadow-lg shadow-amber-900/20 rounded-xl mt-2 group"
                                                        >
                                                            {isSummoning ? (
                                                                <>
                                                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                                                    Intimando...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Megaphone className="h-5 w-5 mr-2 group-hover:animate-bounce" />
                                                                    Confirmar e Intimar Jogador
                                                                </>
                                                            )}
                                                        </Button>
                                                    </div>
                                                </div>

                                                {/* SUMMON HISTORY SECTION */}
                                                <div className="space-y-4 pt-4 border-t border-white/5">
                                                    <div className="flex items-center gap-2 text-amber-500 mb-2">
                                                        <History className="h-5 w-5" />
                                                        <h3 className="font-bold uppercase tracking-widest text-sm">Histórico de Intimações</h3>
                                                    </div>

                                                    <div className="grid gap-4">
                                                        {getUserTickets(selectedSummonUser.discordId)
                                                            .filter(t => t.isSummon || t.title?.startsWith('INTIMAÇÃO:'))
                                                            .length > 0 ? (
                                                            getUserTickets(selectedSummonUser.discordId)
                                                                .filter(t => t.isSummon || t.title?.startsWith('INTIMAÇÃO:'))
                                                                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                                                                .map((summon, idx) => (
                                                                    <div key={idx} className="bg-white/5 border border-white/5 rounded-2xl p-6 space-y-4 hover:border-amber-500/20 transition-all">
                                                                        <div className="flex justify-between items-start gap-4">
                                                                            <div className="space-y-1">
                                                                                <div className="text-white font-bold text-lg">{summon.title?.replace('INTIMAÇÃO: ', '')}</div>
                                                                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                                                                    <User className="h-3 w-3" />
                                                                                    <span>Staff: <span className="text-gray-300">{(summon.logs?.find(l => l.type === 'claim')?.userName) || summon.assignedBy || 'Desconhecido'}</span></span>
                                                                                    <span className="mx-1">•</span>
                                                                                    <Clock className="h-3 w-3" />
                                                                                    <span>{new Date(summon.createdAt).toLocaleString('pt-BR')}</span>
                                                                                </div>
                                                                            </div>
                                                                            <div className="flex gap-2">
                                                                                <Button
                                                                                    size="sm"
                                                                                    onClick={() => window.open(`/staff/tickets/${summon.id}`, '_blank')}
                                                                                    className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-bold h-8 text-xs rounded-lg border-none shadow-lg shadow-amber-900/20 px-4"
                                                                                >
                                                                                    Ver Ticket
                                                                                </Button>
                                                                                {['admin', 'direcao'].includes(user?.accessLevel) && (
                                                                                    <Button
                                                                                        size="sm"
                                                                                        variant="ghost"
                                                                                        onClick={() => handleDeleteSummon(summon.id)}
                                                                                        className="text-red-500 hover:text-white hover:bg-red-600 h-8 text-xs rounded-lg border border-red-500/20 hover:border-red-600"
                                                                                    >
                                                                                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                                                                                        Excluir Histórico
                                                                                    </Button>
                                                                                )}
                                                                            </div>
                                                                        </div>

                                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                                                            <div className="space-y-1">
                                                                                <div className="text-gray-500 uppercase text-[10px] font-bold tracking-wider">Texto Público</div>
                                                                                <p className="text-gray-300 leading-relaxed bg-black/20 p-3 rounded-xl border border-white/5">{summon.description?.replace('**[INTIMAÇÃO OFICIAL]**\n\n', '')}</p>
                                                                            </div>
                                                                            <div className="space-y-1">
                                                                                <div className="text-gray-500 uppercase text-[10px] font-bold tracking-wider">Notas Internas</div>
                                                                                <p className="text-gray-400 italic bg-black/40 p-3 rounded-xl border border-white/5 border-dashed">
                                                                                    {summon.internalNotes || summon.formData?.internalText || 'Nenhuma nota interna registrada.'}
                                                                                </p>
                                                                            </div>
                                                                        </div>

                                                                        {summon.playerId && (
                                                                            <div className="flex items-center gap-2 text-[10px] text-gray-600 uppercase font-black tracking-tighter">
                                                                                <span>Perfil Vinculado: #{summon.playerId} {summon.characterName} ({summon.city})</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))
                                                        ) : (
                                                            <div className="text-center py-12 bg-black/20 rounded-2xl border border-dashed border-white/5">
                                                                <div className="text-gray-600 mb-2">Nenhuma intimação prévia encontrada para este usuário.</div>
                                                                <div className="text-[10px] text-gray-700 uppercase tracking-widest font-bold">Inicie uma nova intimação acima</div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </TabsContent>
                        </motion.div>
                    )}
                    {activeTab === 'player-lookup' && (
                        <motion.div
                            key="player-lookup"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <TabsContent value="player-lookup" className="mt-0">
                                <AnimatePresence mode="wait">
                                    {!selectedPlayerProfile ? (
                                        <motion.div
                                            key="search-view"
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            <Card className="bg-black/40 backdrop-blur-xl border-white/10 shadow-2xl overflow-hidden min-h-[600px]">
                                                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-[100px] -z-10" />
                                                <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-[100px] -z-10" />

                                                <CardHeader className="border-b border-white/5 pb-6">
                                                    <div>
                                                        <CardTitle className="text-2xl font-bold text-white flex items-center gap-3">
                                                            <Search className="h-6 w-6 text-blue-400" />
                                                            Buscar Jogador
                                                        </CardTitle>
                                                        <CardDescription className="text-gray-400 mt-1">
                                                            Pesquise por ID do Discord, Nome do Personagem ou ID do Personagem
                                                        </CardDescription>
                                                    </div>
                                                </CardHeader>

                                                <CardContent className="p-6 space-y-6">
                                                    {/* Search Input */}
                                                    <div className="relative group max-w-2xl mx-auto">
                                                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                                        <div className="relative">
                                                            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-400 transition-colors" />
                                                            <Input
                                                                placeholder="Digite o ID do Discord, nome ou ID do personagem..."
                                                                value={playerSearchQuery}
                                                                onChange={(e) => handlePlayerSearch(e.target.value)}
                                                                className="pl-12 bg-black/50 border-white/10 text-white h-14 rounded-xl focus:border-blue-500/50 focus:ring-blue-500/20 transition-all placeholder:text-gray-600 text-lg"
                                                            />
                                                            {isSearchingPlayers && (
                                                                <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                                                                    <RefreshCw className="h-5 w-5 text-blue-400 animate-spin" />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Search Results */}
                                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
                                                        {playerSearchResults.map((player, idx) => (
                                                            <motion.div
                                                                key={player.discordId}
                                                                initial={{ opacity: 0, y: 10 }}
                                                                animate={{ opacity: 1, y: 0 }}
                                                                transition={{ delay: idx * 0.05 }}
                                                                onClick={() => setSelectedPlayerProfile(player)}
                                                                className="group relative bg-white/5 hover:bg-white/10 border border-white/5 hover:border-blue-500/30 rounded-2xl p-5 transition-all duration-300 cursor-pointer overflow-hidden"
                                                            >
                                                                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />

                                                                <div className="relative flex items-center gap-4">
                                                                    {/* Avatar */}
                                                                    <div className="h-16 w-16 rounded-2xl bg-gray-800 flex items-center justify-center border border-white/10 overflow-hidden group-hover:border-blue-500/50 transition-colors">
                                                                        {player.avatar ? (
                                                                            <img
                                                                                src={`https://cdn.discordapp.com/avatars/${player.discordId}/${player.avatar}.png?size=128`}
                                                                                alt="Avatar"
                                                                                className="w-full h-full object-cover"
                                                                                onError={(e) => {
                                                                                    e.target.style.display = 'none';
                                                                                    e.target.parentElement.querySelector('.fallback-icon').style.display = 'flex';
                                                                                }}
                                                                            />
                                                                        ) : null}
                                                                        <User className="fallback-icon h-8 w-8 text-gray-400" style={{ display: player.avatar ? 'none' : 'flex' }} />
                                                                    </div>

                                                                    <div className="flex-1 min-w-0">
                                                                        <h3 className="text-lg font-bold text-white truncate group-hover:text-blue-400 transition-colors">
                                                                            {player.name}
                                                                        </h3>
                                                                        <div className="flex items-center gap-1.5 text-gray-400 text-xs mt-0.5">
                                                                            <span className="bg-white/10 px-1.5 py-0.5 rounded">ID: {player.discordId}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="relative mt-4 space-y-3">
                                                                    {player.cities && player.cities.length > 0 ? (
                                                                        <div className="space-y-2">
                                                                            {player.cities.map((cityData, cIdx) => (
                                                                                <div key={cIdx} className="bg-black/20 rounded-lg p-2 border border-white/5 flex items-center justify-between">
                                                                                    <div className="flex flex-col">
                                                                                        <span className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">{cityData.city}</span>
                                                                                        <span className="text-sm font-semibold text-gray-200">{cityData.characterName}</span>
                                                                                    </div>
                                                                                    <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-[10px]">
                                                                                        ID: {cityData.playerId}
                                                                                    </Badge>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="text-center py-2 text-gray-500 text-sm italic">
                                                                            Nenhum personagem vinculado
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </motion.div>
                                                        ))}

                                                        {playerSearchResults.length === 0 && playerSearchQuery.length >= 2 && !isSearchingPlayers && (
                                                            <div className="col-span-full py-20 text-center">
                                                                <div className="bg-gray-800/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                                                                    <SearchX className="h-10 w-10 text-gray-600" />
                                                                </div>
                                                                <h3 className="text-xl font-bold text-white">Nenhum jogador encontrado</h3>
                                                                <p className="text-gray-500 mt-2">Tente buscar por outros termos ou verifique se os dados estão corretos.</p>
                                                            </div>
                                                        )}

                                                        {playerSearchQuery.length < 2 && (
                                                            <div className="col-span-full py-20 text-center">
                                                                <div className="bg-blue-500/5 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                                                                    <Search className="h-10 w-10 text-blue-500/30" />
                                                                </div>
                                                                <h3 className="text-xl font-bold text-white/50">Aguardando busca...</h3>
                                                                <p className="text-gray-600 mt-2">Digite pelo menos 2 caracteres para pesquisar.</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </motion.div>
                                    ) : (
                                        <motion.div
                                            key="profile-view"
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: 20 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            <PlayerProfileView
                                                player={selectedPlayerProfile}
                                                onClose={() => setSelectedPlayerProfile(null)}
                                            />
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </TabsContent>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Tabs >

            {/* Modal com Backdrop Blur */}
            < AnimatePresence >
                {isEditingFAQ && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsEditingFAQ(false)}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        />

                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="relative bg-[#0f1115] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden"
                        >
                            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/20 rounded-full blur-[60px]" />
                            <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-500/20 rounded-full blur-[60px]" />

                            <div className="p-6 border-b border-white/10 flex items-center justify-between relative z-10">
                                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                                    <div className="p-2 bg-indigo-500/20 rounded-lg">
                                        {selectedFAQ ? <Edit className="h-5 w-5 text-indigo-400" /> : <Plus className="h-5 w-5 text-indigo-400" />}
                                    </div>
                                    {selectedFAQ ? 'Editar Item' : 'Novo Item do FAQ'}
                                </h2>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setIsEditingFAQ(false)}
                                    className="text-gray-400 hover:text-white hover:bg-white/10 rounded-full w-8 h-8 p-0"
                                >
                                    <X className="h-5 w-5" />
                                </Button>
                            </div>

                            <div className="p-6 space-y-6 relative z-10">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-300 ml-1">Categoria do Tópico</label>
                                    <Select
                                        value={faqForm.category}
                                        onValueChange={(v) => setFaqForm({ ...faqForm, category: v })}
                                    >
                                        <SelectTrigger className="bg-black/40 border-white/10 text-white h-11 rounded-xl focus:ring-indigo-500/30">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#1a1d24] border-white/10 text-white">
                                            <SelectItem value="suporte_duvidas">Dúvidas Gerais</SelectItem>
                                            <SelectItem value="compras_doacoes">Problema com Compras</SelectItem>
                                            <SelectItem value="denuncia_jogador">Denunciar Jogador</SelectItem>
                                            <SelectItem value="contestar_punicao">Revogar Punição</SelectItem>
                                            <SelectItem value="programacao">Problema Técnico / Bug</SelectItem>
                                            <SelectItem value="cupons_descontos">Cupom ou Desconto</SelectItem>
                                            <SelectItem value="denuncia_staff">Denúncia contra Staff</SelectItem>
                                            <SelectItem value="contestar_banimento">Revogar Banimento</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-300 ml-1">Pergunta Frequente</label>
                                    <Input
                                        value={faqForm.question}
                                        onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
                                        placeholder="Ex: Como faço para adquirir moedas VIP?"
                                        className="bg-black/40 border-white/10 text-white h-11 rounded-xl focus:border-indigo-500/50"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-300 ml-1">Resposta Detalhada</label>
                                    <Textarea
                                        value={faqForm.answer}
                                        onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
                                        placeholder="Digite a resposta completa que será exibida para o usuário..."
                                        className="bg-black/40 border-white/10 text-white min-h-[150px] rounded-xl focus:border-indigo-500/50 resize-none p-4"
                                    />
                                </div>
                            </div>

                            <div className="p-6 border-t border-white/10 bg-black/20 flex justify-end gap-3 relative z-10">
                                <Button
                                    variant="outline"
                                    onClick={() => setIsEditingFAQ(false)}
                                    className="border-white/10 text-gray-300 hover:bg-white/5 hover:text-white rounded-xl h-11 px-6 bg-transparent"
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    onClick={handleSaveFAQ}
                                    className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg shadow-indigo-500/25 border-0 rounded-xl h-11 px-6"
                                >
                                    <Save className="h-4 w-4 mr-2" />
                                    {selectedFAQ ? 'Salvar Alterações' : 'Criar Item'}
                                </Button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence >

            {/* Modal de Criação de ID */}
            < AnimatePresence >
                {isCreatingId && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsCreatingId(false)}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        />

                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="relative bg-[#0f1115] border border-white/10 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden"
                        >
                            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/20 rounded-full blur-[60px]" />
                            <div className="absolute bottom-0 left-0 w-32 h-32 bg-teal-500/20 rounded-full blur-[60px]" />

                            <div className="p-6 border-b border-white/10 flex items-center justify-between relative z-10">
                                <h2 className="text-xl font-bold text-white flex items-center gap-3">
                                    <div className="p-2 bg-emerald-500/20 rounded-lg">
                                        <Plus className="h-5 w-5 text-emerald-400" />
                                    </div>
                                    Novo ID
                                </h2>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setIsCreatingId(false)}
                                    className="text-gray-400 hover:text-white hover:bg-white/10 rounded-full w-8 h-8 p-0"
                                >
                                    <X className="h-5 w-5" />
                                </Button>
                            </div>

                            <div className="p-6 space-y-4 relative z-10">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-300 ml-1">Cidade</label>
                                    <Select
                                        value={newIdData.city}
                                        onValueChange={(v) => setNewIdData({ ...newIdData, city: v })}
                                    >
                                        <SelectTrigger className="bg-black/40 border-white/10 text-white h-11 rounded-xl focus:ring-emerald-500/30">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#1a1d24] border-white/10 text-white">
                                            {Object.keys(availableIds).map(city => (
                                                <SelectItem key={city} value={city}>{city}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-300 ml-1">Número do ID</label>
                                    <Input
                                        value={newIdData.id}
                                        onChange={(e) => setNewIdData({ ...newIdData, id: e.target.value })}
                                        placeholder="Ex: 1050"
                                        className="bg-black/40 border-white/10 text-white h-11 rounded-xl focus:border-emerald-500/50"
                                    />
                                </div>
                            </div>

                            <div className="p-6 border-t border-white/10 bg-black/20 flex justify-end gap-3 relative z-10">
                                <Button
                                    variant="outline"
                                    onClick={() => setIsCreatingId(false)}
                                    className="border-white/10 text-gray-300 hover:bg-white/5 hover:text-white rounded-xl h-11 px-6 bg-transparent"
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    onClick={handleCreateId}
                                    className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/25 border-0 rounded-xl h-11 px-6"
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Adicionar
                                </Button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence >

            {/* Modal de Revogação */}
            < AnimatePresence >
                {isRevokeModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsRevokeModalOpen(false)}
                            className="absolute inset-0 bg-black/80 backdrop-blur-md"
                        />

                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="relative bg-[#0f1115] border border-red-500/20 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden"
                        >
                            <div className="absolute top-0 right-0 w-64 h-64 bg-red-500/10 rounded-full blur-[100px] -z-10" />

                            <div className="p-8 space-y-6">
                                <div className="flex flex-col items-center text-center space-y-4">
                                    <div className="p-4 bg-red-500/20 rounded-2xl">
                                        <ShieldAlert className="h-8 w-8 text-red-500" />
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-black text-white">Revogar Punição</h2>
                                        <p className="text-sm text-gray-400 mt-1">
                                            Você está prestes a anular uma punição aplicada. Esta ação ficará registrada no histórico.
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
                                        <p className="text-xs text-gray-500 uppercase font-bold mb-1">Punição Selecionada</p>
                                        <p className="text-white font-medium">{punishmentToRevoke?.punishmentType}</p>
                                        <p className="text-xs text-gray-400 mt-1">{punishmentToRevoke?.publicReason}</p>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-300 ml-1">Motivo da Revogação <span className="text-red-500">*</span></label>
                                        <Textarea
                                            value={revocationReason}
                                            onChange={(e) => setRevocationReason(e.target.value)}
                                            placeholder="Descreve o porquê de estar revogando esta punição..."
                                            className="bg-black/60 border-white/10 text-white min-h-[120px] rounded-2xl focus:border-red-500/50 resize-none p-4"
                                        />
                                    </div>
                                </div>

                                <div className="flex flex-col gap-3">
                                    <Button
                                        onClick={handleConfirmRevocation}
                                        className="w-full h-12 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-bold shadow-lg shadow-red-500/20 rounded-2xl"
                                    >
                                        Confirmar Revogação
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        onClick={() => setIsRevokeModalOpen(false)}
                                        className="w-full h-12 text-gray-400 hover:text-white hover:bg-white/5 rounded-2xl"
                                    >
                                        Cancelar
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence >
            {/* EVIDENCE MODAL (SECURE VIEWER) */}
            < AnimatePresence >
                {evidenceModalOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setEvidenceModalOpen(false)}
                        className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-md flex items-center justify-center p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col relative"
                        >
                            {/* HEADER */}
                            <div className="flex items-center justify-between p-4 border-b border-white/5 bg-black/40">
                                <h3 className="text-white font-bold flex items-center gap-2">
                                    {evidenceType === 'video' ? <FileVideo className="h-4 w-4 text-indigo-400" /> : <FileImage className="h-4 w-4 text-indigo-400" />}
                                    Visualizar Prova (Modo Seguro)
                                </h3>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-gray-400 hover:text-white hover:bg-white/10"
                                        onClick={() => setEvidenceModalOpen(false)}
                                    >
                                        <X className="h-5 w-5" />
                                    </Button>
                                </div>
                            </div>

                            {/* CONTENT */}
                            <div className="flex-1 overflow-auto bg-black/50 flex items-center justify-center p-4 min-h-[400px] relative">

                                {/* LOADING STATE */}
                                {evidenceLoading && (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 z-20">
                                        <RefreshCw className="h-10 w-10 text-indigo-500 animate-spin mb-4" />
                                        <p className="text-gray-300 font-medium">Gerando link seguro temporário...</p>
                                    </div>
                                )}

                                {/* WATERMARK */}
                                <div className="absolute inset-0 pointer-events-none z-10 opacity-20 flex flex-col justify-around py-8 overflow-hidden select-none">
                                    {[1, 2, 3, 4].map((line) => (
                                        <div key={line} className="w-full text-center text-gray-500 font-black text-2xl tracking-widest uppercase">
                                            {staffName || 'STAFF'} - USO INTERNO
                                        </div>
                                    ))}
                                </div>
                                <div className="absolute top-4 left-4 z-20 pointer-events-none opacity-50">
                                    <p className="text-xs text-white/50 font-mono">
                                        Viewer ID: {user?.id?.slice(0, 8)} | {new Date().toLocaleTimeString()}
                                    </p>
                                </div>

                                {/* MEDIA PLAYBACK */}
                                {!evidenceLoading && evidenceUrl && (
                                    <div
                                        className="relative z-0 max-w-full max-h-[80vh] group"
                                        onContextMenu={(e) => e.preventDefault()}
                                    >
                                        {evidenceType === 'video' ? (
                                            evidenceUrl.toLowerCase().endsWith('.mkv') ? (
                                                <div className="flex flex-col items-center justify-center text-center p-8 gap-4 z-20 relative">
                                                    <div className="h-16 w-16 rounded-full bg-yellow-500/10 flex items-center justify-center mb-2">
                                                        <FileVideo className="h-8 w-8 text-yellow-500" />
                                                    </div>
                                                    <h4 className="text-lg font-bold text-white">Formato de Vídeo não Suportado</h4>
                                                    <p className="text-gray-400 max-w-md">
                                                        Arquivos <strong>.MKV</strong> não podem ser reproduzidos diretamente.
                                                    </p>
                                                </div>
                                            ) : (
                                                <video
                                                    src={evidenceUrl}
                                                    controls
                                                    autoPlay
                                                    controlsList="nodownload noremoteplayback"
                                                    disablePictureInPicture
                                                    disableRemotePlayback
                                                    className="max-h-[80vh] max-w-full rounded-lg shadow-lg relative z-0"
                                                    onContextMenu={(e) => e.preventDefault()}
                                                    onError={(e) => {
                                                        toast.error('Erro ao reprodurir o vídeo. O link pode ter expirado ou formato inválido.');
                                                    }}
                                                />
                                            )
                                        ) : (
                                            <img
                                                src={evidenceUrl}
                                                alt="Prova"
                                                className="max-h-[80vh] max-w-full object-contain rounded-lg shadow-lg relative z-0"
                                                onContextMenu={(e) => e.preventDefault()}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    toast.error('Erro ao carregar imagem.');
                                                }}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* FOOTER INFO */}
                            <div className="p-3 bg-white/5 border-t border-white/5 text-center flex justify-center items-center px-6">
                                <p className="text-xs text-indigo-400/50 font-mono">
                                    Secure Link Expires in 5min
                                </p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence >

        </div >
    );
};

export default StaffTools;

